import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { toast } from '../../components/index.js';
import { CAPTURE_BUDGET_MAX_BYTES, captureFrameFromDom } from './feedbackCapture.js';
import { getBuildInfo } from '../../lib/buildInfo.js';
import {
  addFeedbackCapture,
  createFeedbackPackage,
  deleteFeedbackCapture,
  deleteFeedbackPackage,
  finalizeFeedbackPackage,
  getFeedbackPackage,
  listFeedbackPackages,
  patchFeedbackPackage,
  updateFeedbackCapture,
} from './feedbackApi.js';
import {
  createDrawAnnotation,
  createPenAnnotation,
  createPointAnnotation,
  nextAnnotationId,
  nextScreenId,
  normalizeFeedbackPackage,
  normalizeFeedbackStage,
  screenIdForStage,
} from './feedbackAnnotations.js';
import { buildElementSelectionMetadata } from './elementSelection.js';
import { regionToImageCrop } from './feedbackGeometry.js';
import { createSerializedMutationQueue } from './feedbackAutosave.js';
import { reviewToolsEnabled } from './reviewToolsCapability.js';
import {
  clearPendingFeedbackMutation,
  FEEDBACK_MUTATION_CHANGE_EVENT,
  feedbackMutationSignature,
  pendingFeedbackMutationsForActor,
  pendingFeedbackMutation,
  restorePendingFeedbackMutations,
  shouldRetainFeedbackMutation,
} from './feedbackIdempotency.js';
import { prepareFeedbackZipDownload } from './feedbackExport.js';
import { FeedbackDownloadDialog } from './FeedbackDownloadDialog.jsx';
import { buildOutboundCapture, resolveFeedbackPayloadLimits } from './feedbackPayloadBudget.js';
import { fetchClientConfig } from '../../lib/clientConfig.js';
import {
  createInitialFeedbackState,
  feedbackReducer,
  isForeignServerScreen,
  mergePackageBaseWithLocalScreens,
  screenContentFingerprint,
} from './feedbackReducer.js';
import {
  clearFeedbackSessionSnapshot,
  createFeedbackSessionSnapshot,
  feedbackSnapshotHasMeaningfulWork,
  feedbackSnapshotIsRestorable,
  migrateFeedbackSessionSnapshotToV3,
  migrateLegacyFeedbackSessionSnapshot,
  readFeedbackSessionSnapshot,
  writeFeedbackSessionSnapshot,
} from './feedbackStorage.js';
import {
  advanceFeedbackIdentityEpoch,
  readFeedbackIdentityRecord,
  subscribeFeedbackIdentity,
} from './feedbackIdentity.js';
import { registerFeedbackIdentityTransitionHandler } from './feedbackIdentityTransition.js';
import { isFeedbackTestHookEnabled } from './feedbackTestHooks.js';
import { FEEDBACK_STORAGE_KEYS, colorValue } from './feedbackTypes.js';
import './feedbackMarkup.css';

const FeedbackMarkupContext = createContext(null);

function newCreateOperationId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `00000000-0000-4000-8000-${Date.now().toString().padStart(12, '0').slice(-12)}`
  );
}

function blobToDownloadDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error('Could not prepare the ZIP download.'));
    reader.readAsDataURL(blob);
  });
}

function localPackage(user) {
  const now = new Date().toISOString();
  return {
    id: `FP-LOCAL-${Date.now()}`,
    schemaVersion: '1.1',
    title: `Feedback package ${now.slice(0, 16).replace('T', ' ')}`,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    finalizedAt: null,
    createdBy: {
      id: user?.id || 'unknown',
      displayName: user?.displayName || 'Signed-in user',
      persona: user?.persona || 'Shell user',
    },
    overallInstructions: '',
    screenCount: 0,
    annotationCount: 0,
    primaryRoute: null,
    routeList: [],
    screens: [],
    localOnly: true,
    createOperationId: newCreateOperationId(),
  };
}

function titleCaseFromSegment(segment) {
  if (!segment) return '';
  return segment
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function pageTitleFromRoute(pathname) {
  if (!pathname || pathname === '/') return 'Home';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Home';
  return titleCaseFromSegment(segments[segments.length - 1]);
}

function pageTitle(pathname) {
  const docTitle = document.title || '';
  const isGenericBrand = /^DHA PRIME(\s|·|$)/i.test(docTitle.trim());
  const fromTopbar = document.querySelector('.topbar-pagetitle-title')?.textContent?.trim();
  if (fromTopbar) return fromTopbar;
  if (!isGenericBrand && docTitle) return docTitle.trim();
  return pageTitleFromRoute(pathname) || docTitle || 'DHA PRIME';
}

function persistedCaptureIdFor(pkg, screen) {
  if (pkg?.localOnly) return null;
  if (screen?.persistedCaptureId) return screen.persistedCaptureId;
  if (['idle', 'editing', 'save_failed'].includes(screen?.saveStatus)) {
    // A local screen id is not evidence that the capture reached the server.
    // Fresh/edited stages without an explicit durable id must POST on save and
    // delete locally; hydrated server stages always carry persistedCaptureId.
    return null;
  }
  return screen?.screenId || screen?.metadata?.screenId || null;
}

function packageRevision(pkg) {
  return Number.isInteger(Number(pkg?.revision)) ? Number(pkg.revision) : null;
}

function conflictSaveMessage() {
  return 'This package changed in another tab. Your draft is still kept locally.';
}

function conflictBlocksSaveMessage() {
  return 'A screen in this package conflicts with a newer save from another tab. Review that screen before saving the whole draft — saving now would overwrite the other tab’s work.';
}

function unconfirmedCaptureBlocksSaveMessage() {
  return 'A screen has not been confirmed saved. Reopen that screen and try saving it again before saving or ending this draft.';
}

function persistenceConfirmationError(message) {
  const error = new Error(message);
  error.code = 'PERSISTENCE_CONFIRMATION_MISSING';
  return error;
}

// Exported for unit tests (TFB-03 guidance mapping).
export function failureMessageFromError(error) {
  if (error?.code === 'CONFLICT' && error?.details?.reason === 'already_submitted') {
    return 'This package was already submitted to the triage queue.';
  }
  // status 413 covers the nginx edge rejecting the body with an HTML error
  // page — no JSON error code arrives (TFB-03, live 2026-07-23).
  if (error?.code === 'PAYLOAD_TOO_LARGE' || error?.status === 413) {
    return 'This capture is too large to save. Try a smaller capture region.';
  }
  if (error?.status === 422) {
    return 'The server could not accept this capture as sent. Try a smaller region, or remove very dense pen strokes and save again. Your local work is still available.';
  }
  if (error?.code === 'PERSISTENCE_CONFIRMATION_MISSING') {
    return 'The server response could not confirm this screen was saved. Your screenshot is still kept locally; try saving again.';
  }
  const base = 'Draft could not be saved to history. Your local work is still available.';
  return error?.requestId ? `${base} (reference ${error.requestId})` : base;
}

function isNotFoundError(error) {
  return error?.status === 404 || error?.code === 'NOT_FOUND';
}

function rebaseRecoveredScreen(screen) {
  const { persistedCaptureId: _persistedCaptureId, revision: _revision, ...rest } = screen || {};
  return {
    ...rest,
    saveStatus: 'save_failed',
    localRevision: Math.max(1, Number(rest.localRevision) || 0),
    lastServerSaveAt: null,
  };
}

function rebaseRecoveredPackage(pkg) {
  const now = new Date().toISOString();
  const { revision: _revision, serverRevision: _serverRevision, ...rest } = pkg || {};
  const screens = (rest.screens || []).map(rebaseRecoveredScreen);
  return normalizeFeedbackPackage({
    ...rest,
    id: `FP-LOCAL-${Date.now()}`,
    localOnly: true,
    recoveryKind: 'server_missing',
    recoveredFromPackageId: pkg?.id || null,
    updatedAt: now,
    screens,
  });
}

function rebaseRecoveredStage(stage) {
  return stage ? normalizeFeedbackStage(rebaseRecoveredScreen(stage)) : null;
}

function clearPendingMutationsForPackage(actorUserId, packageId) {
  if (!actorUserId || !packageId) return;
  for (const identity of Object.keys(pendingFeedbackMutationsForActor(actorUserId))) {
    const separator = identity.indexOf(':');
    if (separator < 1) continue;
    const operation = identity.slice(0, separator);
    const resourceId = identity.slice(separator + 1);
    if (resourceId !== packageId && !resourceId.startsWith(`${packageId}:`)) continue;
    clearPendingFeedbackMutation({ actorUserId, operation, resourceId });
  }
}

function isWorkspaceRoute(pathname) {
  if (pathname === '/feedback/queue') return false;
  return (
    pathname === '/feedback/workspace' ||
    /^\/feedback\/[^/]+\/edit$/.test(pathname || '') ||
    /^\/feedback\/[^/]+$/.test(pathname || '')
  );
}

function packageIsSubmitted(pkg) {
  return Boolean(pkg?.status === 'submitted' || pkg?.submittedAt);
}

function modeForPackage(pkg, requestedMode = 'edit') {
  if (requestedMode === 'view') return packageIsSubmitted(pkg) ? 'view' : 'edit';
  if (requestedMode === 'edit') return packageIsSubmitted(pkg) ? 'view' : 'edit';
  return packageIsSubmitted(pkg) ? 'view' : 'edit';
}

function routeForPackage(pkg, mode = 'edit') {
  if (!pkg?.id || pkg.localOnly || mode === 'session') {
    return '/feedback/workspace';
  }
  return mode === 'view'
    ? `/feedback/${encodeURIComponent(pkg.id)}`
    : `/feedback/${encodeURIComponent(pkg.id)}/edit`;
}

function hydrateStageFromPackage(pkg, screen) {
  const persistedCaptureId = persistedCaptureIdFor(pkg, screen);
  return normalizeFeedbackStage({
    ...screen,
    id: screenIdForStage(screen),
    persistedCaptureId,
    saveStatus: screen?.saveStatus || (persistedCaptureId ? 'saved' : 'idle'),
    localRevision: Number.isInteger(Number(screen?.localRevision))
      ? Number(screen.localRevision)
      : 0,
    lastServerSaveAt: screen?.lastServerSaveAt || pkg?.updatedAt || screen?.savedAt || null,
  });
}

function newerThan(left, right) {
  const leftTime = left ? Date.parse(left) : NaN;
  const rightTime = right ? Date.parse(right) : NaN;
  if (Number.isNaN(leftTime)) return false;
  if (Number.isNaN(rightTime)) return true;
  return leftTime > rightTime;
}

function waitForPaint() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
      setTimeout(resolve, 0);
      return;
    }
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function buildResumeContext({ feedbackPackage, activeStage, selectedAnnotationId }) {
  const screenId =
    screenIdForStage(activeStage) || feedbackPackage?.resumeContext?.screenId || null;
  if (!screenId) return null;
  return {
    screenId,
    annotationId: selectedAnnotationId || null,
  };
}

function degradedSnapshotRecovery(feedbackPackage, activeStage) {
  const packageScreens = feedbackPackage?.screens || [];
  const lostScreenIds = new Set(
    packageScreens
      .filter((screen) => !screen.screenshotDataUrl)
      .map((screen) => screenIdForStage(screen))
      .filter(Boolean),
  );
  const activeScreenId = screenIdForStage(activeStage);
  if (activeStage && !activeStage.screenshotDataUrl && activeScreenId) {
    lostScreenIds.add(activeScreenId);
  }
  const count = lostScreenIds.size;
  const message = `${count} screen${count === 1 ? '' : 's'} lost ${
    count === 1 ? 'its' : 'their'
  } image on this device. Marks and notes are still available.`;
  return {
    count,
    message,
    feedbackPackage: feedbackPackage
      ? {
          ...feedbackPackage,
          screens: packageScreens.map((screen) => ({
            ...screen,
            imageLostOnDevice: lostScreenIds.has(screenIdForStage(screen)),
          })),
        }
      : feedbackPackage,
    activeStage: activeStage
      ? {
          ...activeStage,
          imageLostOnDevice: lostScreenIds.has(activeScreenId),
        }
      : activeStage,
  };
}

export function FeedbackMarkupProvider({ children }) {
  const [state, dispatch] = useReducer(feedbackReducer, undefined, createInitialFeedbackState);
  const [pendingMutationRevision, setPendingMutationRevision] = useState(0);
  const [preparedExport, setPreparedExport] = useState(null);
  const stateRef = useRef(state);
  // Latest server-confirmed revision per package. stateRef only syncs on the
  // next render, so an async flow that dispatches a server response and then
  // immediately reads stateRef (saveDraft after an autosave flush, finalize
  // after saveDraft) would send a stale expectedRevision and 409 against its
  // own prior write. This ref is updated synchronously at every response site.
  const serverRevisionRef = useRef(null);
  const noteServerRevision = (pkg) => {
    const revision = packageRevision(pkg);
    const tracked = serverRevisionRef.current;
    if (
      pkg?.id &&
      Number.isInteger(revision) &&
      (!tracked ||
        tracked.id !== pkg.id ||
        !Number.isInteger(tracked.revision) ||
        revision >= tracked.revision)
    ) {
      serverRevisionRef.current = { id: pkg.id, revision };
    }
  };
  const isStaleServerResponse = useCallback((pkg) => {
    const revision = packageRevision(pkg);
    const tracked = serverRevisionRef.current;
    return Boolean(
      pkg?.id &&
      Number.isInteger(revision) &&
      tracked?.id === pkg.id &&
      Number.isInteger(tracked.revision) &&
      revision < tracked.revision,
    );
  }, []);
  const expectedRevisionFor = (pkg) => {
    const tracked = serverRevisionRef.current;
    if (pkg?.id && tracked?.id === pkg.id && Number.isInteger(tracked.revision)) {
      return tracked.revision;
    }
    return packageRevision(pkg);
  };
  const launcherRef = useRef(null);
  const endSessionOpenerRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const restoredSnapshotRef = useRef(false);
  const scheduledRevisionRef = useRef(null);
  const closingStageIdRef = useRef(null);
  const historyRequestIdRef = useRef(0);
  const packageRequestIdRef = useRef(0);
  const sessionGenerationRef = useRef(0);
  // Save-and-exit needs the exact synchronous failure reason from saveDraft.
  // Reducer state may not have committed yet when the awaited save returns.
  const lastSaveFailureMessageRef = useRef(null);
  const syncStageSnapshotRef = useRef(async () => {});
  const autosaveQueueRef = useRef(null);
  const deletingScreenIdsRef = useRef(new Set());
  const deletingScreenGenerationsRef = useRef(new Map());
  // screenId -> "sessionGeneration:localRevision" that already got one
  // automatic conflict-recovery retry (prevents refetch/retry loops).
  const conflictRecoveryRef = useRef(new Map());
  // Set by "Exit anyway — keep my work on this device": the active session is
  // cleared but the localStorage snapshot must survive so the work can be
  // resumed later (offline-safe exit; the persistence effect honors this).
  const preserveSnapshotOnClearRef = useRef(false);
  // reconcileRestoredSession is declared later in this component; openFeedback
  // (declared earlier) reaches it through this ref to avoid a TDZ reference.
  const reconcileRestoredSessionRef = useRef(null);
  const elementSelectionRouteRef = useRef(null);
  const pendingNoteFocusIdRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const actorUserId = user?.id || null;
  const activeActorRef = useRef(actorUserId);
  const previousActorRef = useRef(actorUserId);
  const workspaceRouteActive = isWorkspaceRoute(location.pathname);
  // endFeedbackSession runs from async handlers whose closures would otherwise
  // pin a stale location — the ref always reflects the currently mounted route.
  const workspaceRouteActiveRef = useRef(workspaceRouteActive);
  workspaceRouteActiveRef.current = workspaceRouteActive;
  // Round 3 (R3-H01): origin-wide identity epoch and actor-boundary tracking.
  // Every autosave/persistence write and async response is fenced by the
  // stored identity record; a boundary observed in this tab forces explicit
  // resume even for an active snapshot.
  const identityEpochRef = useRef(readFeedbackIdentityRecord()?.identityEpoch || 0);
  const crossedActorBoundaryRef = useRef(false);
  const [resumableDraft, setResumableDraft] = useState(false);
  // Server-published capture byte/point budgets (AR2-M07). Conservative
  // fallbacks apply until /config/client resolves; the server stays
  // authoritative either way.
  const payloadLimitsRef = useRef(resolveFeedbackPayloadLimits(null));
  useEffect(() => {
    let alive = true;
    fetchClientConfig().then((config) => {
      if (alive) payloadLimitsRef.current = resolveFeedbackPayloadLimits(config);
    });
    return () => {
      alive = false;
    };
  }, []);

  stateRef.current = state;

  if (!autosaveQueueRef.current) {
    // One serialized lane for every package mutation (capture saves and the
    // package-level PATCH share a single package revision — concurrency is a
    // guaranteed 409). Snapshots coalesce per screenId so a queued save for
    // one screen is never overwritten by another screen's next snapshot.
    autosaveQueueRef.current = createSerializedMutationQueue(async (snapshot) => {
      await syncStageSnapshotRef.current(snapshot);
    });
  }

  useEffect(() => {
    if (previousActorRef.current === actorUserId && state.sessionActorUserId === actorUserId)
      return;
    // A real actor boundary (one signed-in user replaced by another in this
    // tab) permanently disables silent reactivation of either actor's drafts
    // in this tab — returning work requires the explicit Resume affordance.
    if (previousActorRef.current && actorUserId && previousActorRef.current !== actorUserId) {
      crossedActorBoundaryRef.current = true;
    }
    previousActorRef.current = actorUserId;
    activeActorRef.current = actorUserId;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    autosaveQueueRef.current?.reset?.();
    historyRequestIdRef.current += 1;
    packageRequestIdRef.current += 1;
    sessionGenerationRef.current += 1;
    serverRevisionRef.current = null;
    scheduledRevisionRef.current = null;
    closingStageIdRef.current = null;
    restoredSnapshotRef.current = false;
    dispatch({ type: 'session/reset', actorUserId });
    if (actorUserId) {
      migrateLegacyFeedbackSessionSnapshot();
      migrateFeedbackSessionSnapshotToV3(undefined, actorUserId);
    }
  }, [actorUserId, state.sessionActorUserId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onMutationChange = (event) => {
      if (event.detail?.actorUserId === actorUserId) {
        setPendingMutationRevision((revision) => revision + 1);
      }
    };
    window.addEventListener(FEEDBACK_MUTATION_CHANGE_EVENT, onMutationChange);
    return () => window.removeEventListener(FEEDBACK_MUTATION_CHANGE_EVENT, onMutationChange);
  }, [actorUserId]);

  // Recompute whether the current actor holds an inactive meaningful snapshot
  // worth an explicit "Resume feedback" affordance.
  const refreshResumableDraft = useCallback(() => {
    const owner = activeActorRef.current;
    if (!owner) {
      setResumableDraft(false);
      return;
    }
    const snapshot = readFeedbackSessionSnapshot(undefined, owner);
    const submitted =
      snapshot?.feedbackPackage?.status === 'submitted' ||
      Boolean(snapshot?.feedbackPackage?.submittedAt);
    setResumableDraft(
      Boolean(
        snapshot &&
        !submitted &&
        (snapshot.sessionInactive || snapshot.activationState === 'inactive') &&
        feedbackSnapshotHasMeaningfulWork(snapshot),
      ),
    );
  }, []);

  // Hard identity fence used by both the same-tab transition handler and the
  // cross-tab subscription: cancel timers/queues and invalidate every request
  // generation so no stale callback can write or render outgoing-actor state.
  const fenceIdentityRef = useRef(null);
  fenceIdentityRef.current = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    autosaveQueueRef.current?.reset?.();
    historyRequestIdRef.current += 1;
    packageRequestIdRef.current += 1;
    sessionGenerationRef.current += 1;
    serverRevisionRef.current = null;
    scheduledRevisionRef.current = null;
    closingStageIdRef.current = null;
  };

  // Cross-tab defense (AC25): an identity transition committed in ANOTHER tab
  // fences this tab immediately — before any launcher count or session bar for
  // the incoming actor can render — and can never be undone by stale callbacks
  // because every request generation is invalidated.
  useEffect(() => {
    const unsubscribe = subscribeFeedbackIdentity((record) => {
      const previousEpoch = identityEpochRef.current;
      identityEpochRef.current = Math.max(previousEpoch, record.identityEpoch);
      if (record.identityEpoch <= previousEpoch) return;
      const owner = activeActorRef.current;
      const fencedOut = record.ownerUserId !== owner || record.activationState !== 'active';
      if (!fencedOut) return;
      fenceIdentityRef.current?.();
      // Defensive deactivation: the transitioning tab already persisted the
      // outgoing snapshot inactive; if our copy is still active (we wrote it
      // before the event), flip activation only — never content.
      if (owner) {
        const snapshot = readFeedbackSessionSnapshot(undefined, owner);
        if (snapshot && snapshot.activationState === 'active') {
          if (feedbackSnapshotHasMeaningfulWork(snapshot)) {
            writeFeedbackSessionSnapshot(
              {
                ...snapshot,
                activationState: 'inactive',
                sessionInactive: true,
                identityEpoch: record.identityEpoch,
              },
              undefined,
              owner,
            );
          } else {
            clearFeedbackSessionSnapshot(undefined, owner);
          }
        }
      }
      if (stateRef.current.package || stateRef.current.stage) {
        preserveSnapshotOnClearRef.current = true;
        dispatch({ type: 'session/reset', actorUserId: owner });
      }
      refreshResumableDraft();
    });
    return unsubscribe;
  }, [refreshResumableDraft]);

  // Same-tab identity-transition handler (spec H3): registered with the
  // neutral transition module that CurrentUserContext invokes BEFORE any
  // cookie-changing persona switch or logout.
  useEffect(() => {
    const unregister = registerFeedbackIdentityTransitionHandler({
      getOwnerUserId: () => activeActorRef.current,
      onIdentityTransition: async (record) => {
        identityEpochRef.current = Math.max(identityEpochRef.current, record.identityEpoch);
        fenceIdentityRef.current?.();
        const owner = activeActorRef.current;
        if (!owner) return { ok: true };
        const currentState = stateRef.current;
        const pendingMutations = pendingFeedbackMutationsForActor(owner);
        const hasLiveSession = Boolean(currentState.package || currentState.stage);
        const snapshot = hasLiveSession
          ? createFeedbackSessionSnapshot({
              ownerUserId: owner,
              packageId: currentState.package?.id,
              feedbackPackage: currentState.package,
              activeStage: currentState.stage,
              selectedAnnotationId: currentState.selectedAnnotationId,
              resumeContext: buildResumeContext({
                feedbackPackage: currentState.package,
                activeStage: currentState.stage,
                selectedAnnotationId: currentState.selectedAnnotationId,
              }),
              saveStatus: currentState.saveStatus,
              saveMessage: currentState.saveMessage,
              pendingMutations,
              activationState: 'inactive',
              identityEpoch: record.identityEpoch,
            })
          : readFeedbackSessionSnapshot(undefined, owner);
        const resetSession = () => {
          if (stateRef.current.package || stateRef.current.stage) {
            preserveSnapshotOnClearRef.current = true;
            dispatch({ type: 'session/reset', actorUserId: owner });
          }
          refreshResumableDraft();
        };
        if (!snapshot) {
          clearFeedbackSessionSnapshot(undefined, owner);
          resetSession();
          return { ok: true };
        }
        const submitted =
          snapshot.feedbackPackage?.status === 'submitted' ||
          Boolean(snapshot.feedbackPackage?.submittedAt);
        if (submitted || !feedbackSnapshotHasMeaningfulWork(snapshot)) {
          // An empty (or already-submitted) actor-boundary session leaves no
          // snapshot and no resume affordance behind.
          clearFeedbackSessionSnapshot(undefined, owner);
          resetSession();
          return { ok: true };
        }
        const stored = writeFeedbackSessionSnapshot(
          {
            ...snapshot,
            activationState: 'inactive',
            sessionInactive: true,
            identityEpoch: record.identityEpoch,
          },
          undefined,
          owner,
        );
        if (!stored.ok || (stored.degraded && stored.restorable === false)) {
          // Meaningful local-only work could not be stored restorably: the
          // persona mutation must NOT proceed silently (spec H3).
          return { blocked: true, message: stored.error || stored.warning };
        }
        resetSession();
        return { ok: true };
      },
    });
    return unregister;
  }, [refreshResumableDraft]);

  const ensurePackage = useCallback(
    async (sessionGeneration = sessionGenerationRef.current) => {
      const currentPackage = stateRef.current.package;
      if (currentPackage && !currentPackage.localOnly) return currentPackage;
      let base = currentPackage || localPackage(user);
      if (!base.createOperationId) {
        base = { ...base, createOperationId: newCreateOperationId() };
        dispatch({ type: 'package/set', package: base });
      }
      const response = await createFeedbackPackage(
        {
          title: base.title,
          overallInstructions: base.overallInstructions || '',
          source: 'feedback-markup-session',
        },
        base.createOperationId,
      );
      if (sessionGeneration !== sessionGenerationRef.current) return null;
      const normalized = normalizeFeedbackPackage(response.data);
      noteServerRevision(response.data);
      // The freshly created server package has no screens. If the local-only
      // package already accumulated screens (e.g. earlier saves failed before the
      // package existed server-side), overlay them so the promotion to a server
      // package never wipes local work.
      const merged = mergePackageBaseWithLocalScreens(normalized, base, {
        normalizeBaseScreens: true,
      });
      dispatch({ type: 'package/set', package: merged });
      return merged;
    },
    [user],
  );

  const closeFeedback = useCallback(() => {
    dispatch({ type: 'overlay/close' });
    setTimeout(() => launcherRef.current?.focus?.(), 0);
  }, []);

  // captureStatus is presentation state; this ref is the enforced invariant.
  // Without it, a double-click on the session bar (or menu + bar racing) runs
  // two renders that both compute the same next screen id.
  const captureInFlightRef = useRef(false);
  const startFeedbackSessionRef = useRef(() => {});

  // Capturing while a markup stage is open (e.g. banner capture from the
  // workspace to give feedback ON the feedback tool) must never replace the
  // open stage's unsaved work — persist it exactly like "Done with screen"
  // does, minus the navigation.
  const preserveOpenStageBeforeCapture = useCallback(async () => {
    const currentStage = stateRef.current.stage;
    if (!currentStage) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    let stageToPersist = normalizeFeedbackStage(currentStage);
    const closingScreenId = screenIdForStage(stageToPersist);
    if (
      stageToPersist?.screenshotDataUrl &&
      !stageToPersist.persistedCaptureId &&
      (Number(stageToPersist.localRevision) || 0) === 0
    ) {
      const lastEditedAt = new Date().toISOString();
      stageToPersist = { ...stageToPersist, saveStatus: 'editing', localRevision: 1, lastEditedAt };
      // Mirror the clean capture into the package while the stage is live so a
      // later save failure cannot silently lose it (same rule as completeStage).
      dispatch({
        type: 'stage/status',
        patch: { saveStatus: 'editing', localRevision: 1, lastEditedAt },
      });
    }
    const shouldPersist =
      Boolean(stageToPersist) &&
      (!stageToPersist.persistedCaptureId || stageToPersist.saveStatus !== 'saved') &&
      ((Number(stageToPersist.localRevision) || 0) > 0 || stageToPersist.persistedCaptureId);
    if (!shouldPersist) return;
    if (closingScreenId) closingStageIdRef.current = closingScreenId;
    await autosaveQueueRef.current.submit({
      actorUserId: activeActorRef.current,
      sessionGeneration: sessionGenerationRef.current,
      screenId: closingScreenId,
      screenGeneration: deletingScreenGenerationsRef.current.get(closingScreenId) || 0,
      localRevision: Number(stageToPersist.localRevision) || 0,
      stage: stageToPersist,
    });
  }, []);

  const createStageFromCapture = useCallback(
    async ({
      captureMode,
      region,
      metadataPatch = null,
      buildAnnotations = null,
      selectedAnnotationId = null,
      focusSelectedAnnotationNote = false,
    }) => {
      if (!reviewToolsEnabled()) return null;
      if (captureInFlightRef.current) return;
      captureInFlightRef.current = true;
      // Round 3 adversarial fix (stale-capture fence): snapshot the identity
      // coordinates BEFORE the asynchronous capture. A real persona switch or
      // logout that commits while the capture promise is outstanding advances
      // the epoch/generation — the stale continuation must then be discarded,
      // never installed as a stage or persisted under the incoming actor.
      const captureActorUserId = activeActorRef.current;
      const captureSessionGeneration = sessionGenerationRef.current;
      const captureIdentityEpoch = readFeedbackIdentityRecord()?.identityEpoch ?? null;
      const captureStillCurrent = () => {
        const current =
          captureActorUserId === activeActorRef.current &&
          captureSessionGeneration === sessionGenerationRef.current &&
          (readFeedbackIdentityRecord()?.identityEpoch ?? null) === captureIdentityEpoch;
        // Test-only breadcrumb (same gate as the capture seams): a silently
        // discarded capture is invisible in a trace without naming WHICH fence
        // coordinate moved.
        if (!current && (import.meta.env?.DEV || import.meta.env?.VITE_E2E === 'true')) {
          console.info(
            `[feedback-capture-seam] fence-stale actor=${String(captureActorUserId)}->${String(activeActorRef.current)} gen=${captureSessionGeneration}->${sessionGenerationRef.current} epoch=${String(captureIdentityEpoch)}->${String(readFeedbackIdentityRecord()?.identityEpoch ?? null)}`,
          );
        }
        return current;
      };
      dispatch({ type: 'capture/status', status: 'capturing', message: 'Preparing screenshot...' });
      let frame;
      try {
        // Capture-time budget scales with the server-published SVG text cap.
        // At the default 2 MiB cap this resolves to EXACTLY the historical
        // 1_400_000-byte budget; other server configs scale proportionally.
        frame = await captureFrameFromDom({
          region,
          maxBytes: Math.floor(
            CAPTURE_BUDGET_MAX_BYTES * (payloadLimitsRef.current.maxSvgBytes / (2 * 1024 * 1024)),
          ),
        });
      } catch (err) {
        captureInFlightRef.current = false;
        // A fenced identity means this failure belongs to a session that no
        // longer exists — surface nothing to the incoming actor.
        if (!captureStillCurrent()) return;
        // Region/element captures closed the menu before capturing, so a
        // toast alone would be the only trace of the failure — reopen the
        // menu with the persistent message instead.
        dispatch({
          type: 'capture/status',
          status: 'idle',
          message:
            'Capture could not be completed. Your current feedback package is still open; try capturing again.',
        });
        dispatch({ type: 'overlay/open' });
        toast.error({
          title: 'Capture could not be completed',
          body: err?.message || 'Try capturing again.',
        });
        return;
      }
      // The frame resolved — but only for the identity that requested it.
      if (!captureStillCurrent()) {
        captureInFlightRef.current = false;
        return;
      }
      // The post-capture body is finally-guarded: any unexpected throw after
      // the frame is captured must not leave captureInFlightRef stuck true,
      // which would silently kill both capture entry points until reload.
      try {
        // Persist any open stage BEFORE installing the new one (banner capture
        // from the workspace must not clobber unsaved markup — Codex blocker #3).
        await preserveOpenStageBeforeCapture();
        // Second asynchronous boundary — revalidate before touching state.
        if (!captureStillCurrent()) return;
        const capturedAt = new Date().toISOString();
        const buildInfo = getBuildInfo();
        const openStage = stateRef.current.stage;
        const metadata = {
          // Include the (possibly not-yet-mirrored) open stage in the id pool so
          // the new capture can never collide with the screen being preserved.
          screenId: nextScreenId([
            ...(stateRef.current.package?.screens || []),
            ...(openStage ? [openStage] : []),
          ]),
          captureMode,
          route: location.pathname,
          query: location.search || '',
          hash: location.hash || '',
          pageTitle: pageTitle(location.pathname),
          capturedAt,
          // Captured context (W2): environment + build travel with the screen so
          // triagers see where the evidence came from without asking.
          environmentHost: typeof window !== 'undefined' ? window.location.host : null,
          build: buildInfo?.version || null,
          viewport: frame.viewport,
          image: frame.image,
          captureSource: frame.captureSource,
          fallbackReason: frame.fallbackReason || undefined,
          sourceFrame: frame.sourceFrame || undefined,
          compression: frame.compression || undefined,
          ...(metadataPatch || {}),
          annotations:
            typeof buildAnnotations === 'function'
              ? buildAnnotations({
                  frame,
                  metadata: { ...(metadataPatch || {}), capturedAt },
                })
              : [],
        };
        dispatch({
          type: 'stage/set',
          stage: normalizeFeedbackStage({
            id: metadata.screenId,
            screenId: metadata.screenId,
            screenshotDataUrl: frame.screenshotDataUrl,
            annotatedSvg: '',
            metadata,
            saveStatus: 'idle',
            localRevision: 0,
          }),
        });
        dispatch({ type: 'region/cancel' });
        dispatch({ type: 'overlay/close' });
        dispatch({ type: 'capture/status', status: 'idle', message: null });
        if (selectedAnnotationId) {
          dispatch({ type: 'annotation/select', id: selectedAnnotationId });
          if (focusSelectedAnnotationNote) {
            pendingNoteFocusIdRef.current = selectedAnnotationId;
          }
        }
        navigate('/feedback/workspace');
      } catch (error) {
        if (!captureStillCurrent()) return;
        // Fail-closed sizing (round-2 WS-I): an over-budget capture is rejected
        // HERE, before any stage exists — never admitted into a workspace whose
        // autosaves would then 413 forever.
        const tooLarge = error?.code === 'CAPTURE_TOO_LARGE';
        const message = tooLarge
          ? 'This capture is too large to save. Capture a smaller region or window and try again.'
          : 'Capture could not be completed. Try capturing again.';
        dispatch({
          type: 'capture/status',
          status: 'idle',
          message,
        });
        toast.error({
          title: tooLarge ? 'Capture too large' : 'Capture could not be completed',
          body: tooLarge ? message : error?.message || 'Try capturing again.',
        });
      } finally {
        captureInFlightRef.current = false;
      }
    },
    [location.hash, location.pathname, location.search, navigate, preserveOpenStageBeforeCapture],
  );

  // One-click capture (W1A): grab the current view via DOM render — no stream,
  // no browser share prompt. This is the default path for testers.
  const captureScreen = useCallback(async () => {
    await startFeedbackSessionRef.current();
    return createStageFromCapture({ captureMode: 'visible-viewport' });
  }, [createStageFromCapture]);

  const beginRegionSelection = useCallback(async () => {
    if (!reviewToolsEnabled()) return;
    await startFeedbackSessionRef.current();
    dispatch({ type: 'overlay/close' });
    dispatch({ type: 'region/start' });
  }, []);

  const captureRegion = useCallback(
    async (region) => {
      if (!reviewToolsEnabled()) return;
      if (!region || region.width < 6 || region.height < 6) {
        dispatch({ type: 'region/cancel' });
        return;
      }
      const selectedRegion = { ...region };
      dispatch({ type: 'region/cancel' });
      await waitForPaint();
      await createStageFromCapture({
        captureMode: 'selected-region',
        region: selectedRegion,
      });
    },
    [createStageFromCapture],
  );

  const cancelElementSelection = useCallback(() => {
    elementSelectionRouteRef.current = null;
    dispatch({ type: 'element/cancel' });
  }, []);

  const beginElementSelection = useCallback(async () => {
    if (!reviewToolsEnabled()) return;
    await startFeedbackSessionRef.current();
    elementSelectionRouteRef.current = `${location.pathname}${location.search}${location.hash}`;
    dispatch({ type: 'overlay/close' });
    dispatch({ type: 'region/cancel' });
    dispatch({ type: 'element/start' });
  }, [location.hash, location.pathname, location.search]);

  const captureElementSelection = useCallback(
    async ({ element, bounds }) => {
      if (!reviewToolsEnabled()) return;
      let elementMetadata;
      let elementMetadataLimited = false;
      try {
        elementMetadata = buildElementSelectionMetadata(element, bounds);
        elementMetadataLimited = !elementMetadata.bounds;
      } catch {
        elementMetadata = buildElementSelectionMetadata(null, bounds || null);
        elementMetadataLimited = true;
      }

      cancelElementSelection();
      await waitForPaint();

      const annotationId = elementMetadata.bounds ? nextAnnotationId([]) : null;
      await createStageFromCapture({
        captureMode: 'selected-element',
        metadataPatch: {
          element: elementMetadata,
          ...(elementMetadataLimited ? { elementMetadataLimited: true } : {}),
        },
        buildAnnotations: annotationId
          ? ({ frame }) => {
              const imageBounds = regionToImageCrop(
                elementMetadata.bounds,
                frame.viewport,
                frame.image,
              );
              return [
                createDrawAnnotation({
                  id: annotationId,
                  tool: 'rectangle',
                  start: { x: imageBounds.x, y: imageBounds.y },
                  end: {
                    x: imageBounds.x + imageBounds.width,
                    y: imageBounds.y + imageBounds.height,
                  },
                  image: frame.image,
                  strokeColor: colorValue(stateRef.current.activeColor),
                }),
              ];
            }
          : null,
        selectedAnnotationId: annotationId,
        focusSelectedAnnotationNote: Boolean(annotationId),
      });
    },
    [cancelElementSelection, createStageFromCapture],
  );

  const armTool = useCallback((tool) => dispatch({ type: 'tool/set', tool }), []);
  const clearTool = useCallback(() => dispatch({ type: 'tool/set', tool: null }), []);

  const createPointAnnotationOnStage = useCallback((tool, point, options = {}) => {
    const currentStage = stateRef.current.stage;
    if (!currentStage) return null;
    const annotation = createPointAnnotation({
      id: nextAnnotationId(currentStage.metadata.annotations || []),
      tool,
      point,
      image: currentStage.metadata.image,
      strokeColor: colorValue(stateRef.current.activeColor),
      textBackgroundMode: stateRef.current.activeTextBackgroundMode,
    });
    dispatch({
      type: 'annotation/add',
      annotation,
      startTextEditing: options.startTextEditing,
    });
    return annotation;
  }, []);

  const createDrawAnnotationOnStage = useCallback((tool, start, end) => {
    const currentStage = stateRef.current.stage;
    if (!currentStage) return null;
    const annotation = createDrawAnnotation({
      id: nextAnnotationId(currentStage.metadata.annotations || []),
      tool,
      start,
      end,
      image: currentStage.metadata.image,
      strokeColor: colorValue(stateRef.current.activeColor),
    });
    dispatch({ type: 'annotation/add', annotation });
    return annotation;
  }, []);

  const createPenAnnotationOnStage = useCallback((points) => {
    const currentStage = stateRef.current.stage;
    if (!currentStage) return null;
    const annotation = createPenAnnotation({
      id: nextAnnotationId(currentStage.metadata.annotations || []),
      points,
      image: currentStage.metadata.image,
      strokeColor: colorValue(stateRef.current.activeColor),
    });
    dispatch({ type: 'annotation/add', annotation });
    return annotation;
  }, []);

  const updateAnnotation = useCallback((id, patch) => {
    dispatch({ type: 'annotation/update', id, patch });
  }, []);

  const replaceAnnotations = useCallback((annotations, selectedAnnotationId) => {
    dispatch({ type: 'annotations/replace', annotations, selectedAnnotationId });
  }, []);

  const deleteSelectedAnnotation = useCallback(() => {
    if (stateRef.current.selectedAnnotationId) {
      dispatch({ type: 'annotation/delete', id: stateRef.current.selectedAnnotationId });
    }
  }, []);

  const duplicateSelectedAnnotation = useCallback(() => {
    if (stateRef.current.selectedAnnotationId) {
      dispatch({ type: 'annotation/duplicate', id: stateRef.current.selectedAnnotationId });
    }
  }, []);

  const bringSelectedForward = useCallback(() => {
    if (stateRef.current.selectedAnnotationId) {
      dispatch({ type: 'annotation/bring-forward', id: stateRef.current.selectedAnnotationId });
    }
  }, []);

  const sendSelectedBackward = useCallback(() => {
    if (stateRef.current.selectedAnnotationId) {
      dispatch({ type: 'annotation/send-backward', id: stateRef.current.selectedAnnotationId });
    }
  }, []);

  const startTextEditing = useCallback((id) => {
    dispatch({ type: 'annotation/edit/start', id });
  }, []);

  const stopTextEditing = useCallback(() => {
    dispatch({ type: 'annotation/edit/stop' });
  }, []);

  const flushStageAutosave = useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const currentStage = stateRef.current.stage;
    if (!currentStage) return;
    if (currentStage.saveStatus === 'save_conflict') return;
    // An explicit package-level Save draft must not re-submit a stage that is
    // already settled. Besides wasting a revision, the redundant capture
    // write can leave the visible chip stuck at Saving… while the package
    // patch waits behind it.
    if (currentStage.saveStatus === 'saved') return;
    if ((Number(currentStage.localRevision) || 0) === 0 && !currentStage.persistedCaptureId) return;
    const snapshotStage = normalizeFeedbackStage(currentStage);
    await autosaveQueueRef.current.submit({
      actorUserId: activeActorRef.current,
      sessionGeneration: sessionGenerationRef.current,
      screenId: screenIdForStage(snapshotStage),
      screenGeneration:
        deletingScreenGenerationsRef.current.get(screenIdForStage(snapshotStage)) || 0,
      localRevision: Number(snapshotStage.localRevision) || 0,
      stage: snapshotStage,
    });
  }, []);

  syncStageSnapshotRef.current = async ({
    actorUserId: snapshotActorUserId,
    sessionGeneration: snapshotSessionGeneration,
    stage: stageSnapshot,
    screenId,
    localRevision,
    screenGeneration = 0,
  }) => {
    if (!snapshotActorUserId || snapshotActorUserId !== activeActorRef.current) return;
    if (snapshotSessionGeneration !== sessionGenerationRef.current) return;
    if (
      deletingScreenIdsRef.current.has(screenId) ||
      (deletingScreenGenerationsRef.current.get(screenId) || 0) !== screenGeneration
    )
      return;
    if (!stageSnapshot) return;
    // Identity-epoch fence (AC25): a save may only run for the actor the
    // stored identity record marks active, and its response is discarded if
    // the epoch moved while the request was in flight.
    const identityAtStart = readFeedbackIdentityRecord();
    if (
      identityAtStart &&
      (identityAtStart.ownerUserId !== snapshotActorUserId ||
        identityAtStart.activationState !== 'active')
    )
      return;
    const epochAtStart = identityAtStart?.identityEpoch ?? null;
    const epochStillCurrent = () =>
      (readFeedbackIdentityRecord()?.identityEpoch ?? null) === epochAtStart;
    const startedAt = new Date().toISOString();
    const stageIsClosing = closingStageIdRef.current === screenId;
    const liveStage = stateRef.current.stage;
    if (!stageIsClosing && screenIdForStage(liveStage) === screenId) {
      dispatch({
        type: 'stage/status',
        patch: { saveStatus: 'autosaving', lastAutosaveAt: startedAt },
      });
    }
    try {
      const pkg = await ensurePackage(snapshotSessionGeneration);
      if (!pkg || snapshotSessionGeneration !== sessionGenerationRef.current) return;
      if (snapshotActorUserId !== activeActorRef.current) return;
      if (
        deletingScreenIdsRef.current.has(screenId) ||
        (deletingScreenGenerationsRef.current.get(screenId) || 0) !== screenGeneration
      )
        return;
      const expectedRevision = expectedRevisionFor(pkg);
      if (!Number.isInteger(expectedRevision)) {
        throw new Error('Draft revision is unavailable.');
      }
      // Round 3 (AR2-M07): measure the ACTUAL outbound representation before
      // any network mutation. Pen geometry is simplified only on the outbound
      // copy — the editable stage keeps its full-resolution points. When the
      // payload cannot fit, nothing is sent and the draft stays recoverable.
      const outboundCapture = buildOutboundCapture(stageSnapshot, payloadLimitsRef.current);
      if (!outboundCapture.fits) {
        if (!stageIsClosing && screenIdForStage(stateRef.current.stage) === screenId) {
          dispatch({
            type: 'stage/status',
            patch: { saveStatus: 'save_failed', lastAutosaveAt: startedAt },
          });
        } else if (stageIsClosing) {
          dispatch({
            type: 'package/screen/status',
            screenId,
            patch: { saveStatus: 'save_failed', lastAutosaveAt: startedAt },
          });
        }
        dispatch({ type: 'save/status', status: 'failed', message: outboundCapture.guidance });
        return;
      }
      // The ONE canonical outbound payload (adversarial finding 2): the
      // simplified stage the gate measured is exactly what goes on the wire —
      // metadata and SVG together. The editable stageSnapshot keeps its
      // full-resolution geometry locally.
      const annotatedSvg = outboundCapture.annotatedSvg;
      const outboundMetadata = outboundCapture.stage.metadata;
      const outboundFingerprint = screenContentFingerprint(outboundCapture.stage);
      const response = stageSnapshot.persistedCaptureId
        ? await updateFeedbackCapture(pkg.id, stageSnapshot.persistedCaptureId, {
            expectedRevision,
            resumeContext: buildResumeContext({
              feedbackPackage: stateRef.current.package || pkg,
              activeStage: stageSnapshot,
              selectedAnnotationId: stateRef.current.selectedAnnotationId,
            }),
            metadata: outboundMetadata,
            screenshotPng: stageSnapshot.screenshotDataUrl,
            annotatedSvg,
          })
        : await addFeedbackCapture(pkg.id, {
            expectedRevision,
            resumeContext: buildResumeContext({
              feedbackPackage: stateRef.current.package || pkg,
              activeStage: stageSnapshot,
              selectedAnnotationId: stateRef.current.selectedAnnotationId,
            }),
            metadata: outboundMetadata,
            screenshotPng: stageSnapshot.screenshotDataUrl,
            annotatedSvg,
          });
      if (snapshotActorUserId !== activeActorRef.current) return;
      if (snapshotSessionGeneration !== sessionGenerationRef.current) return;
      if (!epochStillCurrent()) return;
      if (
        isStaleServerResponse(response.data) ||
        deletingScreenIdsRef.current.has(screenId) ||
        (deletingScreenGenerationsRef.current.get(screenId) || 0) !== screenGeneration
      )
        return;
      const normalizedPackage = normalizeFeedbackPackage(response.data);
      const persistedScreen = normalizedPackage.screens?.find(
        (screen) => screenIdForStage(screen) === screenId,
      );
      if (!persistedScreen) {
        throw persistenceConfirmationError(
          'The saved capture was missing from the server response.',
        );
      }
      const confirmedPersistedCaptureId = persistedCaptureIdFor(normalizedPackage, persistedScreen);
      if (!confirmedPersistedCaptureId) {
        throw persistenceConfirmationError(
          'The saved capture did not include a durable identifier.',
        );
      }
      const currentStage = stateRef.current.stage;
      if (!stageIsClosing && screenIdForStage(currentStage) === screenId) {
        dispatch({
          type: 'stage/status',
          patch: {
            saveStatus:
              Number(currentStage.localRevision) === Number(localRevision)
                ? 'saved'
                : currentStage.saveStatus,
            persistedCaptureId: confirmedPersistedCaptureId,
            // Base marker for the three-way merge: the server now holds the
            // OUTBOUND (simplified) copy that was just persisted — recording
            // that fingerprint keeps the echo from reading as a foreign write.
            syncedFingerprint: outboundFingerprint,
            lastAutosaveAt: startedAt,
            lastServerSaveAt: new Date().toISOString(),
          },
        });
      }
      // Keep the package mirror's merge base in step with the server copy as
      // well; a closing stage's mirror is additionally confirmed saved.
      dispatch({
        type: 'package/screen/status',
        screenId,
        patch: stageIsClosing
          ? { saveStatus: 'saved', syncedFingerprint: outboundFingerprint }
          : { syncedFingerprint: outboundFingerprint },
      });
      noteServerRevision(response.data);
      dispatch({
        type: 'package/merge-server',
        package: response.data,
        tombstoneScreenIds: [...deletingScreenIdsRef.current],
        preserveLocalPackageFields: true,
      });
      dispatch({ type: 'save/status', status: 'saved', message: 'Live draft saved.' });
    } catch (error) {
      if (snapshotSessionGeneration !== sessionGenerationRef.current) return;
      if (!epochStillCurrent()) return;
      if (
        deletingScreenIdsRef.current.has(screenId) ||
        (deletingScreenGenerationsRef.current.get(screenId) || 0) !== screenGeneration
      )
        return;
      const currentStage = stateRef.current.stage;
      const conflict = error?.code === 'CONFLICT' && error?.details?.reason === 'revision_mismatch';
      if (!stageIsClosing && screenIdForStage(currentStage) === screenId) {
        dispatch({
          type: 'stage/status',
          patch: {
            saveStatus: conflict ? 'save_conflict' : 'save_failed',
            lastAutosaveAt: startedAt,
          },
        });
      } else if (stageIsClosing) {
        // The stage is already discarded, so flag the mirrored package screen
        // directly — otherwise the tray keeps showing a stale editing status
        // and never offers a retry for the failed save.
        dispatch({
          type: 'package/screen/status',
          screenId,
          patch: {
            saveStatus: conflict ? 'save_conflict' : 'save_failed',
            lastAutosaveAt: startedAt,
          },
        });
      }
      dispatch({
        type: 'save/status',
        status: conflict ? 'conflict' : 'failed',
        message: conflict ? conflictSaveMessage() : failureMessageFromError(error),
      });
      if (conflict) {
        // Auto-recover instead of dead-ending: refresh the server revision,
        // three-way merge the fresh package, and retry this snapshot once per
        // local revision. Real same-screen conflicts stay flagged by the merge.
        const alreadyRetried =
          conflictRecoveryRef.current.get(screenId) ===
          `${snapshotSessionGeneration}:${localRevision}`;
        if (!alreadyRetried) {
          conflictRecoveryRef.current.set(
            screenId,
            `${snapshotSessionGeneration}:${localRevision}`,
          );
          try {
            const packageId = stateRef.current.package?.id;
            if (packageId && !stateRef.current.package?.localOnly) {
              const fresh = await getFeedbackPackage(packageId);
              if (
                snapshotSessionGeneration === sessionGenerationRef.current &&
                !isStaleServerResponse(fresh.data)
              ) {
                noteServerRevision(fresh.data);
                dispatch({
                  type: 'package/merge-server',
                  package: fresh.data,
                  tombstoneScreenIds: [...deletingScreenIdsRef.current],
                  preserveLocalPackageFields: true,
                });
                const liveStage = stateRef.current.stage;
                const freshServerScreen = (fresh.data.screens || []).find(
                  (screen) => screenIdForStage(screen) === screenId,
                );
                // Only retry when the fresh server copy is NOT a foreign write
                // over this screen — flipping back to editing re-arms the
                // debounced autosave, which flushes against the fresh revision.
                if (
                  screenIdForStage(liveStage) === screenId &&
                  !isForeignServerScreen(freshServerScreen, liveStage)
                ) {
                  dispatch({ type: 'stage/status', patch: { saveStatus: 'editing' } });
                }
              }
            }
          } catch {
            // Recovery is best-effort; the conflict status remains visible.
          }
        }
      }
    } finally {
      if (closingStageIdRef.current === screenId) {
        closingStageIdRef.current = null;
      }
    }
  };

  const saveDraft = useCallback(
    async ({ silent = false } = {}) => {
      lastSaveFailureMessageRef.current = null;
      if (['checking', 'error'].includes(stateRef.current.recoveryStatus)) {
        const message =
          stateRef.current.recoveryStatus === 'checking'
            ? 'Wait while PRIME verifies this draft.'
            : 'Retry draft verification before saving.';
        lastSaveFailureMessageRef.current = message;
        dispatch({
          type: 'save/status',
          status: 'failed',
          message,
        });
        return null;
      }
      // Capture content is written only through the capture endpoints. A
      // package metadata save must never proceed while a screen carries an
      // unresolved foreign-write conflict.
      const hasUnresolvedConflict = () =>
        stateRef.current.stage?.saveStatus === 'save_conflict' ||
        (stateRef.current.package?.screens || []).some(
          (screenItem) => screenItem.saveStatus === 'save_conflict',
        );
      if (hasUnresolvedConflict()) {
        const message = conflictBlocksSaveMessage();
        lastSaveFailureMessageRef.current = message;
        dispatch({ type: 'save/status', status: 'conflict', message });
        return null;
      }
      const sessionGeneration = sessionGenerationRef.current;
      await flushStageAutosave();
      if (sessionGeneration !== sessionGenerationRef.current) return null;
      // The flush itself may have just detected a foreign conflict.
      if (hasUnresolvedConflict()) {
        const message = conflictBlocksSaveMessage();
        lastSaveFailureMessageRef.current = message;
        dispatch({ type: 'save/status', status: 'conflict', message });
        return null;
      }
      if (stateRef.current.stage?.saveStatus === 'save_failed') {
        const message = unconfirmedCaptureBlocksSaveMessage();
        lastSaveFailureMessageRef.current = message;
        dispatch({ type: 'save/status', status: 'failed', message });
        return null;
      }
      lastSaveFailureMessageRef.current = null;
      dispatch({ type: 'save/status', status: 'saving', message: 'Saving draft...' });
      try {
        // The package PATCH runs in the same serialized lane as capture
        // autosaves — they share one package revision, and a concurrent
        // capture save would otherwise guarantee a 409.
        const response = await autosaveQueueRef.current.runExclusive(async () => {
          const packageBeforeEnsure = stateRef.current.package;
          const promotedLocalScreenIds = new Set(
            packageBeforeEnsure?.localOnly
              ? (packageBeforeEnsure.screens || []).map(screenIdForStage).filter(Boolean)
              : [],
          );
          let pkg = await ensurePackage(sessionGeneration);
          if (!pkg || sessionGeneration !== sessionGenerationRef.current) return null;
          const packageWithLocalState = stateRef.current.package || packageBeforeEnsure || pkg;
          const pendingScreens = (packageWithLocalState.screens || []).filter((screenItem) => {
            const screenId = screenIdForStage(screenItem);
            return promotedLocalScreenIds.has(screenId) || screenItem.saveStatus === 'save_failed';
          });

          for (const pendingScreen of pendingScreens) {
            const pendingScreenId = screenIdForStage(pendingScreen);
            // Same pre-network budget gate as live autosave (AR2-M07): a
            // pending screen that cannot fit blocks the submission locally
            // with recovery guidance instead of sending a doomed request.
            const outboundPending = buildOutboundCapture(pendingScreen, payloadLimitsRef.current);
            if (!outboundPending.fits) {
              throw new Error(outboundPending.guidance);
            }
            // Transmit exactly what the gate measured (adversarial finding 2):
            // the canonical outbound stage and its serialization — a stale
            // stored annotatedSvg was never measured and must not be preferred.
            const capturePayload = {
              expectedRevision: expectedRevisionFor(pkg),
              resumeContext: buildResumeContext({
                feedbackPackage: packageWithLocalState,
                activeStage: stateRef.current.stage,
                selectedAnnotationId: stateRef.current.selectedAnnotationId,
              }),
              metadata: outboundPending.stage.metadata,
              screenshotPng: pendingScreen.screenshotDataUrl,
              annotatedSvg: outboundPending.annotatedSvg,
            };
            const captureResponse =
              pendingScreen.persistedCaptureId && !promotedLocalScreenIds.has(pendingScreenId)
                ? await updateFeedbackCapture(
                    pkg.id,
                    pendingScreen.persistedCaptureId,
                    capturePayload,
                  )
                : await addFeedbackCapture(pkg.id, capturePayload);
            const confirmedPackage = normalizeFeedbackPackage(captureResponse.data);
            const confirmedScreen = (confirmedPackage.screens || []).find(
              (screenItem) => screenIdForStage(screenItem) === pendingScreenId,
            );
            if (!confirmedScreen || !persistedCaptureIdFor(confirmedPackage, confirmedScreen)) {
              throw persistenceConfirmationError(
                `The server did not confirm saved screen ${pendingScreenId}.`,
              );
            }
            noteServerRevision(captureResponse.data);
            pkg = confirmedPackage;
            // The server confirmed the outbound copy: settle the local screen
            // as saved with the outbound fingerprint as its merge base, so the
            // (simplified) echo below cannot read as a foreign write.
            dispatch({
              type: 'package/screen/status',
              screenId: pendingScreenId,
              patch: {
                saveStatus: 'saved',
                syncedFingerprint: screenContentFingerprint(outboundPending.stage),
              },
            });
            dispatch({
              type: 'package/merge-server',
              package: captureResponse.data,
              tombstoneScreenIds: [...deletingScreenIdsRef.current],
              preserveLocalPackageFields: true,
            });
          }

          const currentPackage = stateRef.current.package || packageWithLocalState || pkg;
          const expectedRevision = expectedRevisionFor(pkg);
          if (!Number.isInteger(expectedRevision)) {
            throw new Error('Draft revision is unavailable.');
          }
          return patchFeedbackPackage(pkg.id, {
            expectedRevision,
            title: currentPackage?.title || pkg.title,
            overallInstructions: currentPackage?.overallInstructions || '',
            status: 'draft',
            resumeContext: buildResumeContext({
              feedbackPackage: currentPackage || pkg,
              activeStage: stateRef.current.stage,
              selectedAnnotationId: stateRef.current.selectedAnnotationId,
            }),
          });
        });
        if (!response) return null;
        if (sessionGeneration !== sessionGenerationRef.current) return null;
        if (!isStaleServerResponse(response.data)) {
          noteServerRevision(response.data);
          dispatch({
            type: 'package/merge-server',
            package: response.data,
            tombstoneScreenIds: [...deletingScreenIdsRef.current],
          });
        }
        dispatch({ type: 'save/status', status: 'saved', message: 'Draft saved.' });
        lastSaveFailureMessageRef.current = null;
        if (!silent) toast.success({ title: 'Draft saved.' });
        return response.data;
      } catch (error) {
        if (sessionGeneration !== sessionGenerationRef.current) return null;
        const conflict =
          error?.code === 'CONFLICT' && error?.details?.reason === 'revision_mismatch';
        const message = conflict ? conflictSaveMessage() : failureMessageFromError(error);
        lastSaveFailureMessageRef.current = message;
        if (conflict && stateRef.current.stage) {
          dispatch({ type: 'stage/status', patch: { saveStatus: 'save_conflict' } });
        }
        dispatch({
          type: 'save/status',
          status: conflict ? 'conflict' : 'failed',
          message,
        });
        if (conflict) {
          try {
            const packageId = stateRef.current.package?.id;
            if (packageId && !stateRef.current.package?.localOnly) {
              const fresh = await getFeedbackPackage(packageId);
              if (
                sessionGeneration === sessionGenerationRef.current &&
                !isStaleServerResponse(fresh.data)
              ) {
                noteServerRevision(fresh.data);
                dispatch({
                  type: 'package/merge-server',
                  package: fresh.data,
                  tombstoneScreenIds: [...deletingScreenIdsRef.current],
                  preserveLocalPackageFields: true,
                });
              }
            }
          } catch {
            // Best-effort rebase: the existing conflict message remains actionable.
          }
        }
        return null;
      }
    },
    [ensurePackage, flushStageAutosave, isStaleServerResponse],
  );

  const loadHistory = useCallback(
    async (query = {}, { append = false } = {}) => {
      const requestActorUserId = actorUserId;
      // The first render after an actor switch still carries the previous
      // reducer state. Defer history I/O until the actor-scoped reset commits.
      if (!requestActorUserId || state.sessionActorUserId !== requestActorUserId) {
        return null;
      }
      const requestId = historyRequestIdRef.current + 1;
      historyRequestIdRef.current = requestId;
      dispatch({ type: 'history/status', status: append ? 'loading-more' : 'loading' });
      try {
        const response = await listFeedbackPackages({ limit: 50, ...query });
        if (
          requestId !== historyRequestIdRef.current ||
          activeActorRef.current !== requestActorUserId
        ) {
          return response;
        }
        const incoming = (response.data || []).map((item) =>
          Array.isArray(item?.screens) ? normalizeFeedbackPackage(item) : item,
        );
        const current = append ? stateRef.current.historyItems || [] : [];
        const items = [...current, ...incoming].filter(
          (item, index, all) => all.findIndex((candidate) => candidate?.id === item?.id) === index,
        );
        dispatch({
          type: 'history/status',
          status: 'loaded',
          appendError: '',
          // The list endpoint returns summary rows (no screens array) whose
          // screenCount/annotationCount are server-computed. Normalizing those
          // through packageWithCounts would recompute the counts from the absent
          // screens and wipe them to zero — only normalize full package payloads.
          items,
          meta: {
            total: response.page?.total ?? 0,
            limit: response.page?.limit ?? 50,
            offset: response.page?.offset ?? null,
            nextCursor: response.page?.nextCursor ?? null,
            hasMore: Boolean(response.page?.hasMore),
            lifecycleCounts: response.meta?.lifecycleCounts || {
              draft: 0,
              submitted: 0,
              resolved: 0,
            },
          },
        });
        return response;
      } catch (err) {
        if (
          requestId !== historyRequestIdRef.current ||
          activeActorRef.current !== requestActorUserId
        ) {
          return null;
        }
        if (append) {
          dispatch({
            type: 'history/status',
            status: 'loaded',
            appendError: err.message || 'Could not load more feedback.',
            items: stateRef.current.historyItems || [],
            meta: stateRef.current.historyMeta,
          });
        } else {
          dispatch({
            type: 'history/status',
            status: 'error',
            error: err.message || 'Could not load feedback history.',
            items: [],
            meta: {
              total: 0,
              limit: 50,
              offset: null,
              nextCursor: null,
              hasMore: false,
              lifecycleCounts: { draft: 0, submitted: 0, resolved: 0 },
            },
          });
        }
        return null;
      }
    },
    [actorUserId, state.sessionActorUserId],
  );

  // Explicitly entering (or resuming) Feedback mode marks this actor as the
  // active feedback identity — the epoch advances only when the stored record
  // does not already show this actor active, so same-actor multi-tab saves are
  // never needlessly fenced.
  const activateFeedbackIdentity = useCallback(() => {
    const owner = activeActorRef.current;
    if (!owner) return Promise.resolve(null);
    const record = readFeedbackIdentityRecord();
    if (record && record.ownerUserId === owner && record.activationState === 'active') {
      return Promise.resolve(record);
    }
    return advanceFeedbackIdentityEpoch({ ownerUserId: owner, activationState: 'active' }).then(
      (committed) => {
        if (committed) {
          identityEpochRef.current = Math.max(identityEpochRef.current, committed.identityEpoch);
        }
        return committed;
      },
    );
  }, []);

  const startFeedbackSession = useCallback(() => {
    const activation = activateFeedbackIdentity();
    if (!stateRef.current.package) {
      dispatch({ type: 'package/set', package: localPackage(user) });
    }
    return activation;
  }, [activateFeedbackIdentity, user]);
  startFeedbackSessionRef.current = startFeedbackSession;

  const openFeedback = useCallback(() => {
    launcherRef.current = document.activeElement;
    dispatch({ type: 'overlay/open' });
    void loadHistory();
  }, [loadHistory]);

  const resumeFeedback = useCallback(() => {
    const snapshot = readFeedbackSessionSnapshot(undefined, activeActorRef.current);
    const preserved = snapshot?.feedbackPackage;
    if (!preserved || preserved.status === 'submitted' || preserved.submittedAt) return false;

    preserveSnapshotOnClearRef.current = false;
    activateFeedbackIdentity();
    const nonRestorableRecovery =
      snapshot.snapshotRecoveryKind === 'non-restorable-trimmed' ||
      preserved.recoveryKind === 'non-restorable-trimmed';
    const degraded = nonRestorableRecovery
      ? degradedSnapshotRecovery(preserved, snapshot.activeStage)
      : null;
    const restoredPackage = normalizeFeedbackPackage(degraded?.feedbackPackage || preserved);
    const restoredStage = degraded?.activeStage
      ? normalizeFeedbackStage(degraded.activeStage)
      : snapshot.activeStage
        ? normalizeFeedbackStage(snapshot.activeStage)
        : null;
    dispatch({ type: 'package/set', package: restoredPackage });
    if (restoredStage) dispatch({ type: 'stage/set', stage: restoredStage });
    if (degraded) {
      dispatch({ type: 'recovery/status', status: 'degraded', message: degraded.message });
    } else if (restoredPackage?.localOnly) {
      dispatch({ type: 'recovery/status', status: 'recovered', message: 'Recovered local draft' });
    } else {
      void reconcileRestoredSessionRef.current?.(restoredPackage, restoredStage);
    }
    setResumableDraft(false);
    dispatch({ type: 'overlay/close' });
    navigate(restoredStage ? '/feedback/workspace' : '/feedback?view=drafts');
    void loadHistory();
    return true;
  }, [activateFeedbackIdentity, loadHistory, navigate]);

  const openHistory = useCallback(() => {
    dispatch({ type: 'overlay/close' });
    dispatch({ type: 'history/close' });
    navigate('/feedback');
    loadHistory();
  }, [loadHistory, navigate]);

  const openReview = useCallback(
    async ({ preserveReturnContext = false } = {}) => {
      // Only the plain flush here: an extra preserve pass would double-submit
      // a non-virgin editing stage and bypass the save_conflict guard. The
      // virgin-stage loss case (C-M3) is covered where the stage is actually
      // REPLACED — openScreenForEditing promotes and persists it before
      // stage/set.
      await flushStageAutosave();
      if (!preserveReturnContext) {
        dispatch({ type: 'review/return-context', context: null });
      }
      dispatch({ type: 'review/open' });
    },
    [flushStageAutosave],
  );

  const openPackage = useCallback(
    async (
      packageId,
      { openMenu = false, openWorkspace = false, openReview = false, mode = 'edit' } = {},
    ) => {
      const requestActorUserId = actorUserId;
      // Deep links can render before a changed authenticated actor has received
      // its reducer reset. Do not fetch or restore package state into that
      // transitional session; the callback is recreated once the reset binds it.
      if (!requestActorUserId || state.sessionActorUserId !== requestActorUserId) {
        return null;
      }
      const requestId = packageRequestIdRef.current + 1;
      packageRequestIdRef.current = requestId;
      const sessionGeneration = sessionGenerationRef.current;
      const currentPackage = stateRef.current.package;
      if (currentPackage?.id === packageId && currentPackage.localOnly) {
        const snapshot = readFeedbackSessionSnapshot(undefined, actorUserId);
        const shouldRestoreStage =
          snapshot?.packageId === packageId && snapshot?.activeStage && !openReview;
        const effectiveMode = modeForPackage(currentPackage, mode);
        dispatch({ type: 'package/set', package: currentPackage });
        dispatch({ type: 'workspace/mode', mode: effectiveMode });
        if (shouldRestoreStage) {
          dispatch({ type: 'stage/set', stage: normalizeFeedbackStage(snapshot.activeStage) });
          if (snapshot?.selectedAnnotationId || snapshot?.resumeContext?.annotationId) {
            dispatch({
              type: 'annotation/select',
              id: snapshot.selectedAnnotationId || snapshot.resumeContext?.annotationId,
            });
          }
          dispatch({
            type: 'save/status',
            status: snapshot.activeStage?.saveStatus === 'save_failed' ? 'failed' : 'saving',
            message:
              snapshot.activeStage?.saveStatus === 'save_failed'
                ? 'Recovered your local draft. It is not saved to the server yet.'
                : 'Recovered your local draft. Syncing continues automatically.',
          });
        } else {
          dispatch({ type: 'save/status', status: 'idle', message: '' });
          dispatch({ type: 'stage/discard' });
        }
        dispatch({ type: 'history/status', status: 'loaded' });
        if (openMenu) dispatch({ type: 'overlay/open' });
        dispatch({ type: 'history/close' });
        if (openWorkspace) navigate(routeForPackage(currentPackage, effectiveMode));
        if (openReview) {
          dispatch({ type: 'review/return-context', context: null });
          dispatch({ type: 'review/open' });
        }
        return;
      }
      dispatch({ type: 'history/status', status: 'loading' });
      try {
        const response = await getFeedbackPackage(packageId);
        if (
          requestId !== packageRequestIdRef.current ||
          sessionGeneration !== sessionGenerationRef.current ||
          activeActorRef.current !== requestActorUserId
        ) {
          return null;
        }
        const serverPackage = normalizeFeedbackPackage(response.data);
        const effectiveMode = modeForPackage(serverPackage, mode);
        // Even when the local snapshot's CONTENT wins below, the server owns
        // the revision — the local snapshot never carries one.
        noteServerRevision(response.data);
        const snapshot = readFeedbackSessionSnapshot(undefined, actorUserId);
        const snapshotPackage =
          snapshot?.packageId === packageId && snapshot?.feedbackPackage
            ? normalizeFeedbackPackage(snapshot.feedbackPackage)
            : null;
        const shouldPreferLocal =
          effectiveMode !== 'view' &&
          Boolean(snapshotPackage) &&
          (snapshot?.activeStage ||
            newerThan(snapshot.lastAutosaveAt || snapshot.savedAt, serverPackage.updatedAt));
        const preferredPackage = shouldPreferLocal ? snapshotPackage : serverPackage;
        const nextPackage = snapshotPackage
          ? mergePackageBaseWithLocalScreens(preferredPackage, snapshotPackage, {
              normalizeBaseScreens: !shouldPreferLocal,
            })
          : preferredPackage;
        dispatch({ type: 'package/set', package: nextPackage });
        dispatch({ type: 'workspace/mode', mode: effectiveMode });
        if (shouldPreferLocal && snapshot?.activeStage) {
          dispatch({ type: 'stage/set', stage: normalizeFeedbackStage(snapshot.activeStage) });
          if (snapshot?.selectedAnnotationId || snapshot?.resumeContext?.annotationId) {
            dispatch({
              type: 'annotation/select',
              id: snapshot.selectedAnnotationId || snapshot.resumeContext?.annotationId,
            });
          }
          dispatch({
            type: 'save/status',
            status: snapshot.activeStage?.saveStatus === 'save_failed' ? 'failed' : 'saving',
            message:
              snapshot.activeStage?.saveStatus === 'save_failed'
                ? 'Recovered your local draft. It is not saved to the server yet.'
                : 'Recovered your local draft. Syncing continues automatically.',
          });
        } else {
          dispatch({ type: 'save/status', status: 'idle', message: '' });
          if (openWorkspace) {
            const resumeContext = nextPackage?.resumeContext || null;
            const resumeScreenId =
              resumeContext?.screenId || screenIdForStage(nextPackage?.screens?.[0]) || null;
            const resumeScreen = nextPackage?.screens?.find(
              (screen) => screenIdForStage(screen) === resumeScreenId,
            );
            if (resumeScreen) {
              dispatch({
                type: 'stage/set',
                stage: hydrateStageFromPackage(nextPackage, resumeScreen),
              });
              if (resumeContext?.annotationId) {
                dispatch({ type: 'annotation/select', id: resumeContext.annotationId });
              }
            } else {
              dispatch({ type: 'stage/discard' });
            }
          } else {
            dispatch({ type: 'stage/discard' });
          }
        }
        dispatch({ type: 'history/status', status: 'loaded' });
        if (openMenu) dispatch({ type: 'overlay/open' });
        dispatch({ type: 'history/close' });
        if (openWorkspace) navigate(routeForPackage(nextPackage, effectiveMode));
        if (openReview) {
          dispatch({ type: 'review/return-context', context: null });
          dispatch({ type: 'review/open' });
        }
      } catch (err) {
        if (
          requestId !== packageRequestIdRef.current ||
          sessionGeneration !== sessionGenerationRef.current ||
          activeActorRef.current !== requestActorUserId
        ) {
          return null;
        }
        dispatch({
          type: 'history/status',
          status: 'error',
          error: err.message || 'Could not open feedback package.',
        });
        return null;
      }
    },
    [actorUserId, navigate, state.sessionActorUserId],
  );

  const invalidateActiveSession = useCallback(() => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    autosaveQueueRef.current?.reset?.();
    packageRequestIdRef.current += 1;
    sessionGenerationRef.current += 1;
    scheduledRevisionRef.current = null;
    closingStageIdRef.current = null;
  }, []);

  const clearActiveSession = useCallback((actorId = activeActorRef.current) => {
    const activePackageId = stateRef.current.package?.id || null;
    clearPendingMutationsForPackage(actorId, activePackageId);
    serverRevisionRef.current = null;
    dispatch({ type: 'session/clear-active' });
    if (actorId) clearFeedbackSessionSnapshot(undefined, actorId);
  }, []);

  const completeSubmittedSession = useCallback(() => {
    const submittingActorUserId = activeActorRef.current;
    invalidateActiveSession();
    clearActiveSession(submittingActorUserId);
  }, [clearActiveSession, invalidateActiveSession]);

  const deleteDraftPackage = useCallback(
    async (packageId) => {
      const deletingActorUserId = activeActorRef.current;
      const activePackage = stateRef.current.package;
      const deletingActivePackage = Boolean(packageId && packageId === activePackage?.id);

      if (deletingActivePackage) invalidateActiveSession();

      let response = { data: { id: packageId, deleted: true, localOnly: true } };
      if (!deletingActivePackage || !activePackage?.localOnly) {
        try {
          response = await deleteFeedbackPackage(packageId);
        } catch (error) {
          if (!isNotFoundError(error)) throw error;
          response = { data: { id: packageId, deleted: true, alreadyMissing: true } };
        }
      }

      if (
        deletingActivePackage &&
        deletingActorUserId === activeActorRef.current &&
        stateRef.current.package?.id === packageId
      ) {
        clearActiveSession(deletingActorUserId);
      }
      return response;
    },
    [clearActiveSession, invalidateActiveSession],
  );

  const openEndSession = useCallback((event) => {
    if (!stateRef.current.package || packageIsSubmitted(stateRef.current.package)) return;
    endSessionOpenerRef.current = event?.currentTarget || document.activeElement;
    dispatch({ type: 'end-session/open' });
  }, []);

  const closeEndSession = useCallback(() => {
    dispatch({ type: 'end-session/close' });
    setTimeout(() => endSessionOpenerRef.current?.focus?.(), 0);
  }, []);

  const endFeedbackSession = useCallback(
    async ({ disposition } = {}) => {
      const pkg = stateRef.current.package;
      if (!pkg) return false;

      // Ending the session while a workspace route is mounted must leave that
      // route BEFORE the session clears — FeedbackWorkspacePage's deep-link
      // loader re-opens the routed package the moment the active session stops
      // matching the URL, which would silently re-arm feedback mode. The dead
      // workspace entry is REPLACED so browser Back cannot land on it and
      // deep-link-resurrect the session the user just ended.
      const leaveWorkspaceRoute = (destination) => {
        if (workspaceRouteActiveRef.current) navigate(destination, { replace: true });
      };

      if (disposition === 'save') {
        dispatch({ type: 'end-session/status', status: 'saving' });
        const saved = await saveDraft({ silent: true });
        if (!saved) {
          const saveFailureMessage = lastSaveFailureMessageRef.current;
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error: saveFailureMessage
              ? `${saveFailureMessage} Your feedback session is still open.`
              : 'The draft could not be saved. Your feedback session is still open.',
          });
          return false;
        }
        const endingActorUserId = activeActorRef.current;
        leaveWorkspaceRoute('/feedback?view=drafts');
        invalidateActiveSession();
        clearActiveSession(endingActorUserId);
        toast.success({ title: 'Draft saved. Feedback session ended.' });
        return true;
      }

      if (disposition === 'discard') {
        dispatch({ type: 'end-session/status', status: 'discarding' });
        try {
          // Leaving first is deliberate: deleteDraftPackage clears the active
          // session mid-flight, and an openPackage kicked off by the still-
          // mounted workspace route would survive that clear (its request
          // guards capture generation values from AFTER the invalidation).
          leaveWorkspaceRoute('/feedback');
          await deleteDraftPackage(pkg.id);
          toast.success({ title: 'Feedback package discarded.' });
          return true;
        } catch (error) {
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error:
              error?.message || 'The package could not be discarded. Your session is still open.',
          });
          return false;
        }
      }

      // A session with no captured screens and no marks has nothing to save —
      // exit is a plain local clear that needs no server.
      if (disposition === 'exit-empty') {
        const endingActorUserId = activeActorRef.current;
        leaveWorkspaceRoute('/feedback');
        invalidateActiveSession();
        clearActiveSession(endingActorUserId);
        toast.success({ title: 'Feedback mode exited.' });
        return true;
      }

      // Explicit degraded exit: this choice is exposed only after the normal
      // keep-local path proved that raw screenshot evidence is already absent.
      // Preserve the meaningful package/mark/note data that remains, mark it
      // inactive for explicit resume, and never imply that screenshots survived.
      if (disposition === 'exit-keep-degraded') {
        const endingActorUserId = activeActorRef.current;
        const currentSnapshot = readFeedbackSessionSnapshot(undefined, endingActorUserId);
        const canKeepDegradedCopy = Boolean(
          currentSnapshot &&
          currentSnapshot.packageId === pkg.id &&
          feedbackSnapshotHasMeaningfulWork(currentSnapshot) &&
          !feedbackSnapshotIsRestorable(currentSnapshot),
        );
        if (!canKeepDegradedCopy) {
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error:
              'PRIME could not verify a recoverable marks-and-notes copy, so the session stays open.',
          });
          return false;
        }

        const inactiveWrite = writeFeedbackSessionSnapshot(
          { ...currentSnapshot, activationState: 'inactive', sessionInactive: true },
          undefined,
          endingActorUserId,
        );
        const inactiveSnapshot = readFeedbackSessionSnapshot(undefined, endingActorUserId);
        const inactiveCopyVerified = Boolean(
          inactiveWrite.ok &&
          inactiveSnapshot &&
          inactiveSnapshot.packageId === pkg.id &&
          inactiveSnapshot.activationState === 'inactive' &&
          inactiveSnapshot.sessionInactive === true &&
          feedbackSnapshotHasMeaningfulWork(inactiveSnapshot),
        );
        if (!inactiveCopyVerified) {
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error:
              'PRIME could not finish preserving the marks-and-notes copy. The session stays open so you can retry.',
            degradedExitAvailable: true,
          });
          return false;
        }

        leaveWorkspaceRoute('/feedback');
        preserveSnapshotOnClearRef.current = true;
        invalidateActiveSession();
        serverRevisionRef.current = null;
        dispatch({ type: 'session/clear-active' });
        refreshResumableDraft();
        toast.success({
          title: 'Feedback mode exited with limited recovery.',
          body: 'Your marks and notes stay on this device and will be offered when you return. Raw screenshots that were already unavailable could not be kept.',
        });
        return true;
      }

      // Offline-safe exit after a failed save: leave feedback mode but keep
      // the local snapshot recoverable. The snapshot is marked inactive so a
      // full reload does NOT silently re-enter feedback mode — the next
      // "Resume feedback" offers the preserved work instead (explicit resume).
      if (disposition === 'exit-keep-local') {
        const endingActorUserId = activeActorRef.current;
        // "Keep my work on this device" is a promise about the CURRENT live
        // state. Always write it at the exit boundary; an older restorable
        // snapshot must never authorize discarding edits that failed to
        // autosave. If the current write fails, keep the session open.
        const liveWrite = writeFeedbackSessionSnapshot(
          createFeedbackSessionSnapshot({
            ownerUserId: endingActorUserId,
            packageId: pkg.id,
            feedbackPackage: pkg,
            activeStage: stateRef.current.stage,
            selectedAnnotationId: stateRef.current.selectedAnnotationId,
          }),
          undefined,
          endingActorUserId,
        );
        const currentSnapshot = readFeedbackSessionSnapshot(undefined, endingActorUserId);
        if (!liveWrite.ok || !currentSnapshot) {
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error:
              'Your work could not be stored on this device, so exiting now would lose it. The session stays open — try saving to the server instead.',
          });
          return false;
        }
        if (!feedbackSnapshotIsRestorable(currentSnapshot)) {
          const degradedExitAvailable =
            currentSnapshot.packageId === pkg.id &&
            feedbackSnapshotHasMeaningfulWork(currentSnapshot);
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error: degradedExitAvailable
              ? 'Some raw screenshots are no longer available. You can exit with the marks and notes that remain on this device, or cancel and keep the session open.'
              : 'A screenshot exists only in this open session. Save it to the server before exiting so no captured evidence is lost.',
            degradedExitAvailable,
          });
          return false;
        }
        const inactiveWrite = writeFeedbackSessionSnapshot(
          { ...currentSnapshot, activationState: 'inactive', sessionInactive: true },
          undefined,
          endingActorUserId,
        );
        if (!inactiveWrite.ok) {
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error:
              'Your work could not be stored on this device, so exiting now would lose it. The session stays open — try saving to the server instead.',
          });
          return false;
        }
        const inactiveSnapshot = readFeedbackSessionSnapshot(undefined, endingActorUserId);
        if (!inactiveSnapshot || !feedbackSnapshotIsRestorable(inactiveSnapshot)) {
          const degradedExitAvailable = Boolean(
            inactiveSnapshot &&
            inactiveSnapshot.packageId === pkg.id &&
            feedbackSnapshotHasMeaningfulWork(inactiveSnapshot),
          );
          dispatch({
            type: 'end-session/status',
            status: 'failed',
            error: degradedExitAvailable
              ? 'Some raw screenshots are no longer available. You can exit with the marks and notes that remain on this device, or cancel and keep the session open.'
              : 'A screenshot exists only in this open session. Save it to the server before exiting so no captured evidence is lost.',
            degradedExitAvailable,
          });
          return false;
        }
        const degradedCopy = Boolean(
          currentSnapshot.snapshotTrimmed || liveWrite.degraded || inactiveWrite.degraded,
        );
        leaveWorkspaceRoute('/feedback');
        preserveSnapshotOnClearRef.current = true;
        invalidateActiveSession();
        serverRevisionRef.current = null;
        dispatch({ type: 'session/clear-active' });
        refreshResumableDraft();
        toast.success({
          title: 'Feedback mode exited.',
          body: degradedCopy
            ? 'Your marks and notes stay on this device and will be offered when you return. The raw screenshots were too large for browser storage and could not be kept.'
            : 'Your unsaved work stays on this device and will be offered when you return.',
        });
        return true;
      }

      return false;
    },
    [
      clearActiveSession,
      deleteDraftPackage,
      invalidateActiveSession,
      navigate,
      refreshResumableDraft,
      saveDraft,
    ],
  );

  const openScreenForEditing = useCallback(
    async (screenId, options = {}) => {
      const currentPackage = stateRef.current.package;
      const screen = currentPackage?.screens?.find((item) => screenIdForStage(item) === screenId);
      if (!screen) return;
      // stage/set REPLACES the open stage: a captured-but-unmarked screen that
      // was never mirrored into the package would be silently destroyed by
      // this jump. Promote and persist it first (same rule as capture).
      const openStage = stateRef.current.stage;
      if (screenIdForStage(openStage) !== screenId) {
        await preserveOpenStageBeforeCapture();
      }
      const mode = options.mode || stateRef.current.workspaceMode || 'edit';
      dispatch({
        type: 'stage/set',
        stage: hydrateStageFromPackage(currentPackage, screen),
      });
      dispatch({ type: 'workspace/mode', mode });
      if (options.selectedAnnotationId) {
        dispatch({ type: 'annotation/select', id: options.selectedAnnotationId });
      }
      navigate(routeForPackage(currentPackage, mode));
    },
    [navigate, preserveOpenStageBeforeCapture],
  );

  const jumpToAnnotationFromReview = useCallback(
    (screenId, annotationId) => {
      const reviewBody = document.querySelector('.feedback-review-body');
      dispatch({
        type: 'review/return-context',
        context: {
          screenId,
          annotationId,
          scrollTop: reviewBody?.scrollTop || 0,
        },
      });
      dispatch({ type: 'review/close' });
      openScreenForEditing(screenId, { selectedAnnotationId: annotationId });
    },
    [openScreenForEditing],
  );

  const completeStage = useCallback(async () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const currentStage = stateRef.current.stage;
    const snapshotStage = currentStage ? normalizeFeedbackStage(currentStage) : null;
    const closingScreenId = screenIdForStage(snapshotStage);
    let stageToPersist = snapshotStage;
    if (
      stageToPersist?.screenshotDataUrl &&
      !stageToPersist.persistedCaptureId &&
      (Number(stageToPersist.localRevision) || 0) === 0
    ) {
      const lastEditedAt = new Date().toISOString();
      stageToPersist = {
        ...stageToPersist,
        saveStatus: 'editing',
        localRevision: 1,
        lastEditedAt,
      };
      // Mirror the clean screenshot into the package while the stage is still
      // live — after stage/discard this patch has no screenId or screenshot to
      // merge onto, so a later save failure would silently lose the capture.
      dispatch({
        type: 'stage/status',
        patch: {
          saveStatus: 'editing',
          localRevision: 1,
          lastEditedAt,
        },
      });
    }
    if (closingScreenId) closingStageIdRef.current = closingScreenId;
    const currentPackage = stateRef.current.package;
    dispatch({ type: 'stage/discard' });
    navigate(routeForPackage(currentPackage, 'edit'));
    const shouldPersistClosingStage =
      Boolean(stageToPersist) &&
      (!stageToPersist.persistedCaptureId || stageToPersist.saveStatus !== 'saved') &&
      ((Number(stageToPersist.localRevision) || 0) > 0 || stageToPersist.persistedCaptureId);
    if (shouldPersistClosingStage) {
      await autosaveQueueRef.current.submit({
        actorUserId: activeActorRef.current,
        sessionGeneration: sessionGenerationRef.current,
        screenId: closingScreenId,
        screenGeneration: deletingScreenGenerationsRef.current.get(closingScreenId) || 0,
        localRevision: Number(stageToPersist.localRevision) || 0,
        stage: stageToPersist,
      });
    } else {
      closingStageIdRef.current = null;
    }
  }, [navigate]);

  const deleteStage = useCallback(async () => {
    const sessionGeneration = sessionGenerationRef.current;
    const currentStage = stateRef.current.stage;
    const currentPackage = stateRef.current.package;
    const screenId = screenIdForStage(currentStage);
    const persistedCaptureId =
      currentStage?.persistedCaptureId || persistedCaptureIdFor(currentPackage, currentStage);
    if (!currentPackage || currentPackage.localOnly || !persistedCaptureId) {
      dispatch({ type: 'package/screen/remove', screenId });
      dispatch({ type: 'stage/discard' });
      navigate(routeForPackage(currentPackage, 'edit'));
      return;
    }
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    // An already-started autosave can advance the package revision before the
    // delete reaches SQL Server. Let that request settle while its response is
    // still allowed to merge, then delete against the newest revision.
    await autosaveQueueRef.current?.flushPending?.();
    if (sessionGeneration !== sessionGenerationRef.current) return;
    const packageAfterAutosave = stateRef.current.package || currentPackage;
    deletingScreenIdsRef.current.add(screenId);
    deletingScreenGenerationsRef.current.set(
      screenId,
      (deletingScreenGenerationsRef.current.get(screenId) || 0) + 1,
    );
    autosaveQueueRef.current?.reset?.();
    const mutationIdentity = {
      actorUserId,
      operation: 'screen-delete',
      resourceId: `${currentPackage.id}:${persistedCaptureId}`,
    };
    const expectedRevision = expectedRevisionFor(packageAfterAutosave);
    const pending = pendingFeedbackMutation({
      ...mutationIdentity,
      signature: feedbackMutationSignature({
        packageId: packageAfterAutosave.id,
        captureId: persistedCaptureId,
        expectedRevision,
      }),
    });
    dispatch({ type: 'stage/status', patch: { saveStatus: 'deleting' } });
    dispatch({ type: 'save/status', status: 'saving', message: 'Deleting…' });
    try {
      const response = await deleteFeedbackCapture(currentPackage.id, persistedCaptureId, {
        expectedRevision,
        idempotencyKey: pending.key,
      });
      if (sessionGeneration !== sessionGenerationRef.current) return;
      if (!isStaleServerResponse(response.data)) {
        noteServerRevision(response.data);
        dispatch({
          type: 'package/merge-server',
          package: response.data,
          tombstoneScreenIds: [...deletingScreenIdsRef.current],
          preserveLocalPackageFields: true,
        });
      }
      dispatch({ type: 'package/screen/remove', screenId });
      dispatch({ type: 'stage/discard' });
      navigate(routeForPackage(currentPackage, 'edit'));
      clearPendingFeedbackMutation(mutationIdentity);
      dispatch({ type: 'save/status', status: 'saved', message: 'Screen deleted.' });
    } catch (error) {
      if (sessionGeneration !== sessionGenerationRef.current) return;
      if (!shouldRetainFeedbackMutation(error)) clearPendingFeedbackMutation(mutationIdentity);
      dispatch({
        type: 'stage/status',
        patch: { saveStatus: currentStage?.saveStatus || 'saved' },
      });
      dispatch({
        type: 'save/status',
        status: 'failed',
        message:
          (error?.code === 'CONFLICT' && error?.details?.reason === 'revision_mismatch') ||
          error?.code === 'REVISION_CONFLICT'
            ? conflictSaveMessage()
            : 'Screen could not be deleted. Nothing was removed; try again.',
      });
    } finally {
      deletingScreenIdsRef.current.delete(screenId);
    }
  }, [actorUserId, isStaleServerResponse, navigate]);

  const exportPackage = useCallback(
    async ({ final = true, returnFocus = null } = {}) => {
      const sessionGeneration = sessionGenerationRef.current;
      const currentPackage = stateRef.current.package;
      if (!currentPackage?.screens?.length) {
        dispatch({
          type: 'export/status',
          status: 'failed',
          message:
            'No screens captured yet. Capture the visible viewport or select a region to begin.',
        });
        return;
      }
      dispatch({ type: 'export/status', status: 'exporting', message: 'Exporting ZIP...' });
      try {
        await flushStageAutosave();
        if (sessionGeneration !== sessionGenerationRef.current) return;
        if (isFeedbackTestHookEnabled(FEEDBACK_STORAGE_KEYS.failExport)) {
          throw new Error('Could not build the ZIP. Your package is still open.');
        }
        let pkg = stateRef.current.package;
        if (final) {
          const saved = await saveDraft();
          if (sessionGeneration !== sessionGenerationRef.current) return;
          if (saved) {
            const finalized = await finalizeFeedbackPackage(saved.id, {
              expectedRevision: expectedRevisionFor(saved),
              exportedAt: new Date().toISOString(),
              screenCount: saved.screenCount,
              annotationCount: saved.annotationCount,
            });
            if (sessionGeneration !== sessionGenerationRef.current) return;
            pkg = normalizeFeedbackPackage(finalized.data);
            noteServerRevision(finalized.data);
            dispatch({ type: 'package/set', package: pkg });
          }
        }
        const prepared = await prepareFeedbackZipDownload(pkg, {
          finalStatus: final ? 'final' : pkg.status || 'draft',
        });
        if (sessionGeneration !== sessionGenerationRef.current) return;
        // A data URL preserves a real user-click download path in embedded
        // browser shells that do not surface blob: downloads. The prepared
        // archive remains local to this page and is released with the dialog.
        const url = await blobToDownloadDataUrl(prepared.blob);
        if (sessionGeneration !== sessionGenerationRef.current) return;
        setPreparedExport({
          url,
          filename: prepared.filename,
          exportedAt: prepared.exportedAt,
          finalStatus: final ? 'final' : pkg.status || 'draft',
          started: false,
          returnFocus,
        });
        dispatch({
          type: 'export/status',
          status: 'ready',
          message: 'ZIP ready — select Download ZIP to save it.',
        });
      } catch (err) {
        if (sessionGeneration !== sessionGenerationRef.current) return;
        dispatch({
          type: 'export/status',
          status: 'failed',
          message: err.message || 'Could not build the ZIP. Your package is still open.',
        });
      }
    },
    [flushStageAutosave, saveDraft],
  );

  const closePreparedExport = useCallback(() => {
    setPreparedExport(null);
  }, []);

  const startPreparedExportDownload = useCallback(() => {
    setPreparedExport((current) => (current ? { ...current, started: true } : current));
    dispatch({
      type: 'export/status',
      status: 'exported',
      message: 'Download requested in your browser. Confirm the file appears before relying on it.',
    });
  }, []);

  useEffect(() => {
    const url = preparedExport?.url;
    return () => {
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, [preparedExport?.url]);

  const reconcileRestoredSession = useCallback(async (restoredPackage, restoredStage) => {
    if (!restoredPackage?.id || restoredPackage.localOnly) return restoredPackage;
    const requestActorUserId = activeActorRef.current;
    const requestId = packageRequestIdRef.current + 1;
    packageRequestIdRef.current = requestId;
    const sessionGeneration = sessionGenerationRef.current;
    dispatch({ type: 'recovery/status', status: 'checking', message: 'Checking saved draft…' });

    try {
      const response = await getFeedbackPackage(restoredPackage.id);
      if (
        requestId !== packageRequestIdRef.current ||
        sessionGeneration !== sessionGenerationRef.current ||
        requestActorUserId !== activeActorRef.current
      ) {
        return null;
      }
      const serverPackage = normalizeFeedbackPackage(response.data);
      noteServerRevision(response.data);
      // If the server already accepted this package (a submit whose response
      // was lost), the local "draft" is a phantom: adopt the immutable server
      // copy instead of resurrecting an un-savable, un-discardable session.
      if (serverPackage.status === 'submitted') {
        dispatch({ type: 'package/set', package: serverPackage });
        dispatch({ type: 'stage/discard' });
        dispatch({ type: 'recovery/status', status: 'verified', message: '' });
        return serverPackage;
      }
      const trimmedRecovery = restoredPackage.recoveryKind === 'server-recoverable-trimmed';
      const nonRestorableRecovery = restoredPackage.recoveryKind === 'non-restorable-trimmed';
      const snapshotTrimmedRecovery = trimmedRecovery || nonRestorableRecovery;
      const rehydratedLocalPackage = snapshotTrimmedRecovery
        ? {
            ...restoredPackage,
            screens: (restoredPackage.screens || []).map((localScreen) => {
              const screenId = screenIdForStage(localScreen);
              const serverScreen = (serverPackage.screens || []).find(
                (item) => screenIdForStage(item) === screenId,
              );
              return serverScreen
                ? {
                    ...localScreen,
                    screenshotDataUrl:
                      localScreen.screenshotDataUrl || serverScreen.screenshotDataUrl || null,
                    annotatedSvg: localScreen.annotatedSvg || serverScreen.annotatedSvg || null,
                    persistedCaptureId:
                      localScreen.persistedCaptureId || screenIdForStage(serverScreen),
                  }
                : localScreen;
            }),
          }
        : restoredPackage;
      const shouldPreferLocal =
        !trimmedRecovery &&
        (Boolean(restoredStage) || newerThan(restoredPackage.updatedAt, serverPackage.updatedAt));
      const preferredPackage = shouldPreferLocal ? rehydratedLocalPackage : serverPackage;
      const mergedPackage = mergePackageBaseWithLocalScreens(
        preferredPackage,
        rehydratedLocalPackage,
        {
          normalizeBaseScreens: !shouldPreferLocal,
        },
      );
      const reconciledPackage = {
        ...mergedPackage,
        revision: serverPackage.revision,
        localOnly: false,
        recoveryKind: null,
      };
      let reconciledStage = restoredStage;
      if (snapshotTrimmedRecovery && restoredStage) {
        const restoredScreenId = screenIdForStage(restoredStage);
        const rehydratedScreen = reconciledPackage.screens?.find(
          (screen) => screenIdForStage(screen) === restoredScreenId,
        );
        if (rehydratedScreen) {
          reconciledStage = normalizeFeedbackStage({
            ...restoredStage,
            screenshotDataUrl:
              restoredStage.screenshotDataUrl || rehydratedScreen.screenshotDataUrl || null,
            annotatedSvg: restoredStage.annotatedSvg || rehydratedScreen.annotatedSvg || null,
            persistedCaptureId:
              restoredStage.persistedCaptureId || screenIdForStage(rehydratedScreen),
          });
        }
      }
      if (nonRestorableRecovery) {
        const degraded = degradedSnapshotRecovery(reconciledPackage, reconciledStage);
        const degradedPackage = {
          ...degraded.feedbackPackage,
          recoveryKind: 'non-restorable-trimmed',
        };
        dispatch({ type: 'package/set', package: degradedPackage });
        if (degraded.activeStage) {
          dispatch({ type: 'stage/set', stage: normalizeFeedbackStage(degraded.activeStage) });
        }
        dispatch({
          type: 'recovery/status',
          status: 'degraded',
          message: degraded.message,
        });
        return degradedPackage;
      } else {
        dispatch({ type: 'package/set', package: reconciledPackage });
        if (snapshotTrimmedRecovery && reconciledStage) {
          dispatch({ type: 'stage/set', stage: reconciledStage });
        }
        dispatch({ type: 'recovery/status', status: 'verified', message: '' });
      }
      return reconciledPackage;
    } catch (error) {
      if (
        requestId !== packageRequestIdRef.current ||
        sessionGeneration !== sessionGenerationRef.current ||
        requestActorUserId !== activeActorRef.current
      ) {
        return null;
      }
      if (isNotFoundError(error)) {
        const recoveredPackage = rebaseRecoveredPackage(restoredPackage);
        const recoveredStage = rebaseRecoveredStage(restoredStage);
        serverRevisionRef.current = null;
        dispatch({ type: 'package/set', package: recoveredPackage });
        if (recoveredStage) dispatch({ type: 'stage/set', stage: recoveredStage });
        dispatch({
          type: 'save/status',
          status: 'failed',
          message:
            'Recovered local draft. Its server copy is no longer available. Save it as a new draft or discard it.',
        });
        dispatch({
          type: 'recovery/status',
          status: 'recovered',
          message: 'Recovered local draft',
        });
        return recoveredPackage;
      }
      dispatch({
        type: 'recovery/status',
        status: 'error',
        message: 'Could not verify this draft right now. Your local work is still available.',
      });
      dispatch({
        type: 'save/status',
        status: 'failed',
        message: 'Could not verify this draft right now. Your local work is still available.',
      });
      return null;
    }
  }, []);

  reconcileRestoredSessionRef.current = reconcileRestoredSession;

  const retrySessionReconciliation = useCallback(() => {
    const pkg = stateRef.current.package;
    if (!pkg || pkg.localOnly) return Promise.resolve(pkg || null);
    return reconcileRestoredSession(pkg, stateRef.current.stage);
  }, [reconcileRestoredSession]);

  useEffect(() => {
    if (!actorUserId || restoredSnapshotRef.current) return;
    restoredSnapshotRef.current = true;
    migrateLegacyFeedbackSessionSnapshot();
    migrateFeedbackSessionSnapshotToV3(undefined, actorUserId);
    const snapshot = readFeedbackSessionSnapshot(undefined, actorUserId);
    restorePendingFeedbackMutations(actorUserId, snapshot?.pendingMutations);
    if (!snapshot) {
      refreshResumableDraft();
      return;
    }
    const submitted =
      snapshot.feedbackPackage?.status === 'submitted' ||
      Boolean(snapshot.feedbackPackage?.submittedAt);
    // Round 3 (R3-H01): automatic restore is reserved for SAME-ACTOR crash
    // recovery of an ACTIVE snapshot. Anything else — an explicitly inactive
    // snapshot, an actor boundary observed in this tab, or an identity record
    // showing this actor is not the active identity — requires the explicit
    // Resume affordance (meaningful work) or is removed (empty session).
    const identityRecord = readFeedbackIdentityRecord();
    const identityAllowsAuto =
      !identityRecord ||
      (identityRecord.ownerUserId === actorUserId && identityRecord.activationState === 'active');
    const snapshotActive = snapshot.activationState
      ? snapshot.activationState === 'active'
      : !snapshot.sessionInactive;
    if (!snapshotActive || !identityAllowsAuto || crossedActorBoundaryRef.current) {
      if (submitted || !feedbackSnapshotHasMeaningfulWork(snapshot)) {
        clearFeedbackSessionSnapshot(undefined, actorUserId);
        refreshResumableDraft();
        return;
      }
      if (snapshotActive) {
        // Deactivate the durable copy so every tab agrees this draft needs an
        // explicit resume; content is preserved untouched.
        writeFeedbackSessionSnapshot(
          { ...snapshot, activationState: 'inactive', sessionInactive: true },
          undefined,
          actorUserId,
        );
      }
      preserveSnapshotOnClearRef.current = true;
      refreshResumableDraft();
      return;
    }
    // A submitted package has nothing left to edit — restoring it as the
    // active session would shadow the server's submitted copy on My feedback
    // with a stale "draft" card (W4 fix).
    if (submitted) {
      return;
    }
    let restoredPackage = null;
    let restoredStage = null;
    if (snapshot.feedbackPackage) {
      restoredPackage = normalizeFeedbackPackage(snapshot.feedbackPackage);
      if (
        snapshot.snapshotRecoveryKind === 'non-restorable-trimmed' ||
        restoredPackage.recoveryKind === 'non-restorable-trimmed'
      ) {
        const degraded = degradedSnapshotRecovery(restoredPackage, snapshot.activeStage);
        restoredPackage = normalizeFeedbackPackage(degraded.feedbackPackage);
        restoredStage = degraded.activeStage ? normalizeFeedbackStage(degraded.activeStage) : null;
        dispatch({
          type: 'recovery/status',
          status: 'degraded',
          message: degraded.message,
        });
      }
      dispatch({
        type: 'package/set',
        package: restoredPackage,
      });
    }
    if (snapshot.activeStage) {
      restoredStage = restoredStage || normalizeFeedbackStage(snapshot.activeStage);
      dispatch({ type: 'stage/set', stage: restoredStage });
      if (snapshot?.selectedAnnotationId || snapshot?.resumeContext?.annotationId) {
        dispatch({
          type: 'annotation/select',
          id: snapshot.selectedAnnotationId || snapshot.resumeContext?.annotationId,
        });
      }
      dispatch({
        type: 'save/status',
        status: snapshot.activeStage?.saveStatus === 'save_failed' ? 'failed' : 'saving',
        message:
          snapshot.activeStage?.saveStatus === 'save_failed'
            ? 'Recovered your local draft. It is not saved to the server yet.'
            : 'Recovered your local draft. Syncing continues automatically.',
      });
    }
    if (restoredPackage?.localOnly) {
      if (restoredPackage.recoveryKind !== 'non-restorable-trimmed') {
        dispatch({
          type: 'recovery/status',
          status: restoredPackage.recoveryKind === 'server_missing' ? 'recovered' : 'verified',
          message: restoredPackage.recoveryKind === 'server_missing' ? 'Recovered local draft' : '',
        });
      }
    } else if (restoredPackage) {
      void reconcileRestoredSession(restoredPackage, restoredStage);
    }
  }, [actorUserId, reconcileRestoredSession, refreshResumableDraft]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (state.stage && workspaceRouteActive) {
      document.body.classList.add('feedback-stage-active');
      return () => document.body.classList.remove('feedback-stage-active');
    }
    document.body.classList.remove('feedback-stage-active');
    return undefined;
  }, [state.stage, workspaceRouteActive]);

  useEffect(() => {
    if (!state.elementSelecting) return;
    const selectionRoute = elementSelectionRouteRef.current;
    const currentRoute = `${location.pathname}${location.search}${location.hash}`;
    if (selectionRoute && selectionRoute !== currentRoute) {
      elementSelectionRouteRef.current = null;
      dispatch({ type: 'element/cancel' });
    }
  }, [location.hash, location.pathname, location.search, state.elementSelecting]);

  useEffect(() => {
    const annotationId = pendingNoteFocusIdRef.current;
    if (!annotationId || !workspaceRouteActive || state.selectedAnnotationId !== annotationId) {
      return undefined;
    }
    const frame = requestAnimationFrame(() => {
      const selectorId =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(annotationId)
          : annotationId.replace(/["\\]/g, '\\$&');
      const note = document.querySelector(`[data-testid="feedback-annotation-note-${selectorId}"]`);
      if (note instanceof HTMLElement) {
        note.focus({ preventScroll: true });
        pendingNoteFocusIdRef.current = null;
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [state.selectedAnnotationId, state.stage, workspaceRouteActive]);

  useEffect(() => {
    if (
      state.workspaceMode === 'view' ||
      !actorUserId ||
      activeActorRef.current !== actorUserId ||
      state.sessionActorUserId !== actorUserId
    )
      return;
    const pendingMutations = pendingFeedbackMutationsForActor(actorUserId);
    if (!state.package && !state.stage && Object.keys(pendingMutations).length === 0) {
      // "Exit anyway — keep my work on this device" clears the active session
      // while deliberately leaving the snapshot recoverable.
      if (!preserveSnapshotOnClearRef.current) {
        clearFeedbackSessionSnapshot(undefined, actorUserId);
      }
      return;
    }
    // Identity fence (AC25): only the identity the stored record marks active
    // may persist ACTIVE session state. A live session under a stale/foreign
    // record first self-activates (epoch advance) — the write retries on the
    // next state change once the record reflects this actor.
    const identityRecord = readFeedbackIdentityRecord();
    if (
      identityRecord &&
      (identityRecord.ownerUserId !== actorUserId || identityRecord.activationState !== 'active')
    ) {
      if (identityRecord.ownerUserId === actorUserId || !identityRecord.ownerUserId) {
        void advanceFeedbackIdentityEpoch({
          ownerUserId: actorUserId,
          activationState: 'active',
        }).then((record) => {
          if (record) {
            identityEpochRef.current = Math.max(identityEpochRef.current, record.identityEpoch);
          }
        });
      }
      return;
    }
    preserveSnapshotOnClearRef.current = false;
    const stored = writeFeedbackSessionSnapshot(
      createFeedbackSessionSnapshot({
        ownerUserId: actorUserId,
        packageId: state.package?.id,
        feedbackPackage: state.package,
        activeStage: state.stage,
        selectedAnnotationId: state.selectedAnnotationId,
        resumeContext: buildResumeContext({
          feedbackPackage: state.package,
          activeStage: state.stage,
          selectedAnnotationId: state.selectedAnnotationId,
        }),
        currentRoute: `${location.pathname}${location.search || ''}${location.hash || ''}`,
        saveStatus: state.saveStatus,
        saveMessage: state.saveMessage,
        pendingMutations,
        activationState: 'active',
        identityEpoch: identityRecord?.identityEpoch ?? (identityEpochRef.current || null),
      }),
      undefined,
      actorUserId,
    );
    const storageMessage = stored.ok ? stored.warning : stored.error;
    if (storageMessage && state.storageWarning !== storageMessage) {
      dispatch({ type: 'storage/warn', message: storageMessage });
    }
  }, [
    dispatch,
    location.hash,
    location.pathname,
    location.search,
    state.package,
    state.selectedAnnotationId,
    state.saveMessage,
    state.saveStatus,
    state.stage,
    state.storageWarning,
    actorUserId,
    pendingMutationRevision,
    state.sessionActorUserId,
    state.workspaceMode,
  ]);

  useEffect(() => {
    if (state.workspaceMode === 'view') {
      scheduledRevisionRef.current = null;
      return undefined;
    }
    if (state.recoveryStatus === 'checking' || state.recoveryStatus === 'error') {
      scheduledRevisionRef.current = null;
      return undefined;
    }
    const currentStage = state.stage;
    if (!currentStage) {
      scheduledRevisionRef.current = null;
      return undefined;
    }
    if (currentStage.saveStatus === 'save_conflict') return undefined;
    const currentRevision = Number(currentStage.localRevision) || 0;
    if (currentRevision === 0 && currentStage.saveStatus === 'saved') {
      scheduledRevisionRef.current = currentRevision;
      return undefined;
    }
    if (currentRevision === 0 && !currentStage.persistedCaptureId) return undefined;
    if (scheduledRevisionRef.current === currentRevision && currentStage.saveStatus === 'saved') {
      return undefined;
    }
    scheduledRevisionRef.current = currentRevision;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      void flushStageAutosave();
    }, 550);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [flushStageAutosave, state.recoveryStatus, state.stage, state.workspaceMode]);

  useEffect(() => {
    const stageInteractionsActive =
      workspaceRouteActive && Boolean(state.stage) && state.workspaceMode !== 'view';
    if (
      !(
        state.overlayOpen ||
        stageInteractionsActive ||
        state.regionSelecting ||
        state.elementSelecting ||
        state.reviewOpen
      )
    ) {
      return undefined;
    }
    const onKeyDown = (event) => {
      const target = event.target;
      const editing =
        state.textEditingAnnotationId || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);
      if (event.key === 'Escape') {
        if (state.textEditingAnnotationId) dispatch({ type: 'annotation/edit/stop' });
        else if (stageInteractionsActive && state.activeTool)
          dispatch({ type: 'tool/set', tool: null });
        else if (state.elementSelecting) cancelElementSelection();
        else if (state.regionSelecting) dispatch({ type: 'region/cancel' });
        else if (state.reviewOpen) dispatch({ type: 'review/close' });
        else if (state.overlayOpen) closeFeedback();
      }
      if (editing) return;
      if (
        stageInteractionsActive &&
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 'z'
      ) {
        event.preventDefault();
        dispatch({ type: event.shiftKey ? 'history/redo' : 'history/undo' });
      }
      if (
        stageInteractionsActive &&
        (event.key === 'Delete' || event.key === 'Backspace') &&
        state.selectedAnnotationId
      ) {
        event.preventDefault();
        dispatch({ type: 'annotation/delete', id: state.selectedAnnotationId });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    closeFeedback,
    cancelElementSelection,
    state.activeTool,
    state.elementSelecting,
    state.overlayOpen,
    state.regionSelecting,
    state.reviewOpen,
    state.selectedAnnotationId,
    state.stage,
    state.textEditingAnnotationId,
    state.workspaceMode,
    workspaceRouteActive,
  ]);

  const value = useMemo(
    () => ({
      state: { ...state, resumableDraft },
      dispatch,
      openFeedback,
      resumeFeedback,
      startFeedbackSession,
      closeFeedback,
      captureScreen,
      beginRegionSelection,
      captureRegion,
      beginElementSelection,
      cancelElementSelection,
      captureElementSelection,
      armTool,
      clearTool,
      createPointAnnotationOnStage,
      createDrawAnnotationOnStage,
      createPenAnnotationOnStage,
      updateAnnotation,
      replaceAnnotations,
      deleteSelectedAnnotation,
      duplicateSelectedAnnotation,
      bringSelectedForward,
      sendSelectedBackward,
      startTextEditing,
      stopTextEditing,
      flushStageAutosave,
      completeStage,
      deleteStage,
      saveDraft,
      openHistory,
      openReview,
      loadHistory,
      openPackage,
      deleteDraftPackage,
      openEndSession,
      closeEndSession,
      endFeedbackSession,
      completeSubmittedSession,
      retrySessionReconciliation,
      openScreenForEditing,
      jumpToAnnotationFromReview,
      exportPackage,
      navigate,
    }),
    [
      armTool,
      beginElementSelection,
      beginRegionSelection,
      bringSelectedForward,
      cancelElementSelection,
      captureElementSelection,
      captureRegion,
      captureScreen,
      clearTool,
      closeFeedback,
      completeSubmittedSession,
      completeStage,
      createDrawAnnotationOnStage,
      createPenAnnotationOnStage,
      createPointAnnotationOnStage,
      deleteSelectedAnnotation,
      deleteDraftPackage,
      deleteStage,
      duplicateSelectedAnnotation,
      endFeedbackSession,
      exportPackage,
      flushStageAutosave,
      loadHistory,
      navigate,
      openFeedback,
      resumeFeedback,
      startFeedbackSession,
      openEndSession,
      openHistory,
      openReview,
      openPackage,
      openScreenForEditing,
      jumpToAnnotationFromReview,
      replaceAnnotations,
      retrySessionReconciliation,
      saveDraft,
      sendSelectedBackward,
      startTextEditing,
      state,
      resumableDraft,
      stopTextEditing,
      updateAnnotation,
      closeEndSession,
    ],
  );

  return (
    <FeedbackMarkupContext.Provider value={value}>
      {children}
      <FeedbackDownloadDialog
        download={preparedExport}
        onDownload={startPreparedExportDownload}
        onClose={closePreparedExport}
      />
    </FeedbackMarkupContext.Provider>
  );
}

export function useFeedbackMarkup() {
  const ctx = useContext(FeedbackMarkupContext);
  if (!ctx) throw new Error('useFeedbackMarkup must be used inside FeedbackMarkupProvider');
  return ctx;
}
